import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSidePanel } from '../../context/SidePanelContext';
import { Select, Spark } from '../ui';
import CompoundPill from '../ui/CompoundPill';
import IconRefresh from '../ui/IconRefresh';
import InfoBtn from '../ui/InfoBtn';
import { getAssetRS, getFoStockRS, getNiftyHistory, getTopdownRS } from '../../api/client';

// ── Module-level cache — survives sub-tab switches ────────────────────────────
// Data is fetched once per session; switching away and back is instant.
// Cache is only set when BOTH asset and stock data are present.
// clearRSCache() is exported so the parent Refresh button can bust it.
let _RS_CACHE = null; // { allData, allStockData, computedAt, stale }
export function clearRSCache() { _RS_CACHE = null; }

// Module-level section open/closed state — survives sub-tab switches so the
// user doesn't have to re-expand tables every time they switch away and back.
let _RS_SECTIONS = {
  barChartOpen:  true,
  chartsOpen:    false,
  legendOpen:    false,
  rankingsOpen:  false,
  rsSummaryOpen: false,
  topdownOpen:   true,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getCSSVar = v => getComputedStyle(document.documentElement).getPropertyValue(v).trim();

// Simple MA calculator
export function ma(arr, period) {
  return arr.map((_, i) => {
    if (i < period - 1) return null;
    const slice = arr.slice(i - period + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / period;
  });
}

// ── SidePanel compatibility export ───────────────────────────────────────────
// SidePanel.jsx imports getRSByName to draw a per-ticker RS chart.
// Real data wiring for the side panel is a future step; for now return an empty
// series so the panel renders without crashing.
export function getRSByName(name) {
  return { series: [], asset: { name, cat: 'Stock' } };
}

// ── API data transformation helpers ──────────────────────────────────────────

/**
 * Transform a backend asset entry (from /api/rs/assets) into the shape
 * expected by RSCard, RSChart, and RSTable.
 *
 * Backend rs_series values are normalised to 100 at start.
 * We divide by 100 so the series stays in the ~1.0 range that rsStats() expects.
 */
function transformAsset(a) {
  const series  = (a.rs_series || []).map(d => ({ v: d.rs / 100, date: d.date }));
  const asset   = { id: a.id, name: a.label, cat: a.category };
  const isProxy = a.proxy === true;

  // Proxy entries (e.g. G-Sec bonds) — no yfinance data available
  if (isProxy) {
    return {
      asset,
      series:  [],
      isProxy: true,
      stats: { last: null, maLast: null, ret1W: null, ret1M: null, ret3M: null,
               aboveMA: null, trend: 'Flat', signal: 'Flat', spark: [] },
    };
  }

  // Backend returned an error (no_data / insufficient_data / fetch failure).
  // Show a clean N/A card rather than the misleading RS=1.000 / Neutral default
  // that results from undefined scalars falling through to the scalar fallback.
  if (a.error) {
    return {
      asset,
      series:  [],
      isError: true,
      stats: { last: null, maLast: null, ret1W: null, ret1M: null, ret3M: null,
               aboveMA: null, trend: 'Flat', signal: 'Flat', spark: [] },
    };
  }

  if (series.length >= 22) {
    const computed = rsStats(series);
    // Prefer backend scalars for RS change values and above_ma determination
    const ret1W  = a.rs_5d    ?? computed.ret1W;
    const ret1M  = a.rs_1m    ?? computed.ret1M;
    const ret3M  = a.rs_3m    ?? computed.ret3M;
    const aboveMA = a.above_ma != null ? a.above_ma : computed.aboveMA;
    // Signal is canonical from backend (_infer_signal, ±0.5% band).
    // If absent (stale cache), keep null — render site shows '—', not a re-derived verdict.
    const signal   = a.signal ?? null;
    return { asset, series, building: !!a.building, stats: { ...computed, ret1W, ret1M, ret3M, aboveMA, signal } };
  }

  // Insufficient-series fallback — build stats from backend scalars
  const rs1m = a.rs_1m ?? null;
  const rs5d = a.rs_5d ?? null;
  const last  = (a.rs_current ?? 100) / 100;
  const maLast = (a.ma_current ?? 100) / 100;
  const aboveMA = a.above_ma ?? null;
  const trend   = rs1m > 0.5 ? 'Rising' : rs1m < -0.5 ? 'Falling' : 'Flat';
  // Signal is canonical from backend. Null if absent — render site shows '—'.
  const signal  = a.signal ?? null;
  return {
    asset,
    series,
    building: !!a.building,
    stats: { last, maLast, ret1W: rs5d, ret1M: rs1m, ret3M: null, aboveMA, trend, signal, spark: [] },
  };
}

/**
 * Transform a backend stock entry (from /api/rs/fo-stocks) into a StockRSTable row.
 * No series available — stats derived from backend scalars.
 * rs_5d / rs_1m / rs_3m are absolute RS-point changes on a scale anchored at 100,
 * so they numerically equal % changes to good approximation.
 */
function transformStock(s) {
  const rs5d  = s.rs_5d  ?? null;
  const rs1m  = s.rs_1m  ?? null;
  const rs3m  = s.rs_3m  ?? null;
  // Preserve null so building rows render '—' in Ratio, not the synthetic 1.000 default.
  const last    = s.rs_current != null ? s.rs_current / 100 : null;
  const maLast  = s.ma_current != null ? s.ma_current / 100 : null;
  const aboveMA = s.above_ma ?? null;
  const trend   = rs5d > 0.5 ? 'Rising' : rs5d < -0.5 ? 'Falling' : 'Flat';
  // Signal is canonical from backend. Null if absent — render site shows '—'.
  const signal  = s.signal ?? null;

  // Use real RS series from backend if available (last 30 bars, normalised to /100)
  // Falls back to synthetic 3-point path if series not yet in API response.
  const rawSeries = s.rs_series || [];
  const spark = rawSeries.length >= 2
    ? rawSeries.map(d => d.rs / 100)
    : rs3m != null && rs1m != null && rs5d != null
      ? [last - rs3m / 100, last - rs1m / 100, last - rs5d / 100, last]
      : rs1m != null && rs5d != null
        ? [last - rs1m / 100, last - rs5d / 100, last]
        : [];

  // Full series for the side panel mini-chart
  const series = rawSeries.map(d => ({ v: d.rs / 100, date: d.date }));

  return {
    name:     s.symbol,
    sector:   s.sector || 'Other',
    building: !!s.building,
    series,
    stats:  {
      ret1W: rs5d, ret1M: rs1m, ret3M: rs3m,
      last, trend, signal, spark,
      price1W: s.price_1w ?? null,
      price1M: s.price_1m ?? null,
      price3M: s.price_3m ?? null,
    },
  };
}

export function rsStats(series) {
  if (!series || series.length < 2) {
    return { last:1, maLast:1, ret1W:null, ret1M:null, ret3M:null, aboveMA:null, trend:'Flat', signal:'Flat', spark:[] };
  }
  const vals = series.map(d => d.v);
  const maVals = ma(vals, 20);
  const last    = vals[vals.length - 1];
  const maLast  = maVals[maVals.length - 1];
  const prev20  = vals[vals.length - 21] || vals[0];
  const ret1W   = vals.length >= 6  ? ((last / vals[vals.length - 6])  - 1) * 100 : null;
  const ret1M   = vals.length >= 22 ? ((last / vals[vals.length - 22]) - 1) * 100 : null;
  const ret3M   = vals.length >= 64 ? ((last / vals[vals.length - 64]) - 1) * 100 : null;
  const aboveMA = last > maLast;
  const trend   = last > prev20 ? 'Rising' : last < prev20 ? 'Falling' : 'Flat';
  // Three-tier signal: ±0.5% neutral band around the 20-day MA
  const signal  = _rsSignal(last, maLast);
  const spark   = vals.slice(-20);
  return { last, maLast, ret1W, ret1M, ret3M, aboveMA, trend, signal, spark };
}

// ── RS Chart — canvas, green above MA / red below, black MA line ─────────────
function drawRSChart(canvas, series, isDark, crosshairDate) {
  if (!canvas || !series || series.length < 22) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width) return;
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:6, r:6, t:8, b:8 };

  const vals  = series.map(d => d.v);
  const maV   = ma(vals, 20);
  const valid = maV.map((v, i) => v !== null ? { v, rs: vals[i] } : null).filter(Boolean);
  const allV  = [...vals, ...valid.map(d => d.v)];
  const mn = Math.min(...allV), mx = Math.max(...allV), rng = mx - mn || 0.01;
  const startIdx = maV.findIndex(v => v !== null); // first valid MA index = 19

  const px = i => pad.l + ((i - startIdx) / (vals.length - 1 - startIdx)) * (W - pad.l - pad.r);
  const py = v => pad.t + (1 - (v - mn) / rng) * (H - pad.t - pad.b);

  ctx.clearRect(0, 0, W, H);

  // Horizontal reference line at RS=1 (parity with NIFTY)
  const refY = py(1.0);
  if (refY >= pad.t && refY <= H - pad.b) {
    ctx.beginPath();
    ctx.setLineDash([4, 3]);
    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
    ctx.lineWidth = 0.7;
    ctx.moveTo(pad.l, refY);
    ctx.lineTo(W - pad.r, refY);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    ctx.fillStyle = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
    ctx.textAlign = 'right';
    ctx.fillText('1.0', W - pad.r - 1, refY - 2);
  }

  // RS line — draw segment by segment, green when above MA, red when below
  for (let i = startIdx + 1; i < vals.length; i++) {
    const above = vals[i] >= maV[i];
    ctx.beginPath();
    ctx.moveTo(px(i - 1), py(vals[i - 1]));
    ctx.lineTo(px(i), py(vals[i]));
    ctx.strokeStyle = above ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */;
    ctx.lineWidth = 1.8;
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 0.9;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // MA line — slate in light mode, light-grey in dark mode; both fully opaque enough to read.
  ctx.beginPath();
  let first = true;
  for (let i = startIdx; i < vals.length; i++) {
    const x = px(i), y = py(maV[i]);
    first ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    first = false;
  }
  ctx.strokeStyle = isDark ? 'rgba(200,200,200,0.9)' : 'rgba(30,30,30,0.85)';
  ctx.lineWidth = 1.2;
  ctx.lineJoin = 'round';
  ctx.stroke();

  // ── Crosshair ──
  if (crosshairDate && series.length > 0) {
    const dates = series.map(d => d.date);
    let ci = _closestIdx(dates, crosshairDate);
    if (ci < startIdx) ci = startIdx;
    const cx = px(ci), cy = py(vals[ci]);
    const isAbove = vals[ci] >= (maV[ci] ?? vals[ci]);
    ctx.save();
    // Vertical rule
    ctx.beginPath();
    ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1; ctx.setLineDash([3,2]); ctx.stroke(); ctx.setLineDash([]);
    // Dot on line
    ctx.beginPath(); ctx.arc(cx, cy, 3.5, 0, Math.PI*2);
    ctx.fillStyle = isAbove ? '#3f6b4d' : '#8f4a44'; ctx.fill();

    const onRight = cx < W * 0.6;
    const labelX = cx + (onRight ? 7 : -7);
    const fgVal  = '#111111';
    const fgDate = '#444444';

    // RS value label
    const valStr = vals[ci].toFixed(3);
    ctx.font = 'bold 10px "IBM Plex Mono",monospace';
    const vw = ctx.measureText(valStr).width;
    const vx = onRight ? labelX : labelX - vw;
    ctx.fillStyle = fgVal;
    ctx.textAlign = 'left';
    ctx.fillText(valStr, vx, cy - 6);

    // Date label at bottom
    const dateStr = series[ci].date ?? '';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    const dw = ctx.measureText(dateStr).width;
    const dx = onRight ? labelX : labelX - dw;
    const dateY = H - pad.b - 2;
    ctx.fillStyle = fgDate;
    ctx.fillText(dateStr, dx, dateY);

    ctx.restore();
  }
}

// ── Shared crosshair helper — finds closest index by date ────────────────────
function _closestIdx(dates, targetDate) {
  const target = new Date(targetDate).getTime();
  let best = 0, bestDiff = Infinity;
  dates.forEach((d, i) => {
    const diff = Math.abs(new Date(d).getTime() - target);
    if (diff < bestDiff) { bestDiff = diff; best = i; }
  });
  return best;
}

// ── Price Chart (Nifty 50) — canvas, simple filled line ──────────────────────
function drawPriceChart(canvas, rows, isDark, crosshairDate) {
  if (!canvas || !rows || rows.length < 2) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width) return;
  canvas.width  = rect.width  * dpr;
  canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;
  const pad = { l:6, r:6, t:8, b:8 };

  const vals = rows.map(r => r.close).filter(v => v != null && !isNaN(v));
  if (vals.length < 2) return;
  const n  = vals.length;
  const mn = Math.min(...vals), mx = Math.max(...vals), rng = mx - mn || 1;

  const px = i => pad.l + (i / (n - 1)) * (W - pad.l - pad.r);
  const py = v => pad.t + (1 - (v - mn) / rng) * (H - pad.t - pad.b);

  const isUp = vals[n - 1] >= vals[0];
  const lineColor  = isUp ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */;
  const fillColor  = isUp ? 'rgba(26,106,26,.07)' : 'rgba(196,26,26,.07)';

  ctx.clearRect(0, 0, W, H);

  // Filled area under the line
  ctx.beginPath();
  ctx.moveTo(px(0), py(vals[0]));
  for (let i = 1; i < n; i++) ctx.lineTo(px(i), py(vals[i]));
  ctx.lineTo(px(n - 1), H - pad.b);
  ctx.lineTo(px(0), H - pad.b);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();

  // Price line
  ctx.beginPath();
  ctx.moveTo(px(0), py(vals[0]));
  for (let i = 1; i < n; i++) ctx.lineTo(px(i), py(vals[i]));
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 1.8;
  ctx.lineJoin = 'round';
  ctx.globalAlpha = 0.9;
  ctx.stroke();
  ctx.globalAlpha = 1;

  // ── Crosshair ──
  if (crosshairDate) {
    const dates = rows.map(r => r.date);
    const ci = _closestIdx(dates, crosshairDate);
    const cx = px(ci), cy = py(vals[ci]);
    ctx.save();
    // Vertical rule
    ctx.beginPath();
    ctx.moveTo(cx, pad.t); ctx.lineTo(cx, H - pad.b);
    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1; ctx.setLineDash([3,2]); ctx.stroke(); ctx.setLineDash([]);
    // Dot on line
    ctx.beginPath(); ctx.arc(cx, cy, 3.5, 0, Math.PI*2);
    ctx.fillStyle = isUp ? '#3f6b4d' : '#8f4a44'; ctx.fill();

    const onRight = cx < W * 0.6;
    const labelX = cx + (onRight ? 7 : -7);
    const fgVal  = '#111111';
    const fgDate = '#444444';

    // Price value label
    const valStr = vals[ci].toLocaleString('en-IN', {maximumFractionDigits:0});
    ctx.font = 'bold 10px "IBM Plex Mono",monospace';
    const vw = ctx.measureText(valStr).width;
    const vx = onRight ? labelX : labelX - vw;
    ctx.fillStyle = fgVal;
    ctx.textAlign = 'left';
    ctx.fillText(valStr, vx, cy - 6);

    // Date label at bottom
    const dateStr = rows[ci].date ?? '';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    const dw = ctx.measureText(dateStr).width;
    const dx = onRight ? labelX : labelX - dw;
    const dateY = H - pad.b - 2;
    ctx.fillStyle = fgDate;
    ctx.fillText(dateStr, dx, dateY);

    ctx.restore();
  }
}

// ── Nifty 50 Price Card ───────────────────────────────────────────────────────
function NiftyPriceCard({ history, crosshairDate, onCrosshairChange, rank }) {
  const canvasRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');

  const rows = useMemo(() => (history || []).filter(r => r.close != null), [history]);
  const last   = rows.length > 0 ? rows[rows.length - 1].close : null;
  const first  = rows.length > 0 ? rows[0].close : null;
  const chgPct = first && last ? ((last - first) / first) * 100 : null;

  const draw = useCallback(() => {
    drawPriceChart(canvasRef.current, rows, isDark, crosshairDate);
  }, [rows, isDark, crosshairDate]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (canvasRef.current) ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current || rows.length < 2) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const n = rows.length;
    const pad = 6;
    const i = Math.round((mouseX - pad) / (rect.width - pad * 2) * (n - 1));
    const date = rows[Math.max(0, Math.min(n - 1, i))]?.date;
    if (date) onCrosshairChange?.(date);
  }, [rows, onCrosshairChange]);

  const handleMouseLeave = useCallback(() => onCrosshairChange?.(null), [onCrosshairChange]);

  return (
    <div className="card" style={{padding:'8px 10px', overflow:'hidden'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5}}>
        <div style={{display:'flex', alignItems:'center', gap:6}}>
          {rank != null && (
            <span style={{
              fontSize:15, fontWeight:800, width:26, height:26, borderRadius:'50%',
              display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              ...(rank===1 ? {background:'rgba(245,158,11,.15)', color:'#d97706'} :
                  rank===2 ? {background:'rgba(59,130,246,.12)',  color:'#2563eb'} :
                  rank===3 ? {background:'rgba(249,115,22,.12)',  color:'#ea580c'} :
                             {background:'rgba(148,163,184,.12)', color:'#64748b'}),
            }}>
              {rank}
            </span>
          )}
          <span style={{fontSize:11, fontWeight:700, color:'var(--amber-ink)'}}>NIFTY 50</span>
          <span style={{fontSize:11, color:'var(--ink-3)'}}>Price · 1Y</span>
        </div>
        {last != null && (
          <span style={{fontSize:11, fontFamily:'var(--font-data)', fontWeight:600, color:'var(--ink)'}}>
            {last.toLocaleString('en-IN', {maximumFractionDigits:0})}
          </span>
        )}
      </div>
      <div style={{height:160, position:'relative', cursor:'crosshair'}}
        onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        {rows.length < 2
          ? <div style={{height:'100%', display:'flex', alignItems:'center', justifyContent:'center',
              color:'var(--ink-3)', fontSize:11, background:'var(--surface-2)', borderRadius:3}}>Loading…</div>
          : <canvas ref={canvasRef} style={{width:'100%', height:'100%', display:'block'}}/>
        }
      </div>
      <div style={{display:'flex', gap:10, marginTop:5, fontSize:11, color:'var(--ink-3)'}}>
        <span>1Y <span style={{fontFamily:'var(--font-data)', fontWeight:600,
          color: chgPct == null ? 'var(--ink-3)' : chgPct >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}}>
          {chgPct != null ? `${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(1)}%` : '—'}
        </span></span>
        <span style={{marginLeft:'auto', color:'var(--ink-3)'}}>benchmark</span>
      </div>
    </div>
  );
}

function RSChart({ series, label, stats, onOpen, asset, isProxy, isError, building, chartHeight = 80,
                   crosshairDate, onCrosshairChange, rank }) {
  const canvasRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');

  const draw = useCallback(() => {
    drawRSChart(canvasRef.current, series, isDark, crosshairDate);
  }, [series, isDark, crosshairDate]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (canvasRef.current) ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current || series.length < 22) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const vals = series.map(d => d.v);
    const maV  = ma(vals, 20);
    const startIdx = maV.findIndex(v => v !== null);
    const n = vals.length;
    const pad = 6;
    const rawI = startIdx + (mouseX - pad) / (rect.width - pad * 2) * (n - 1 - startIdx);
    const i = Math.max(startIdx, Math.min(n - 1, Math.round(rawI)));
    const date = series[i]?.date;
    if (date) onCrosshairChange?.(date);
  }, [series, onCrosshairChange]);

  const handleMouseLeave = useCallback(() => onCrosshairChange?.(null), [onCrosshairChange]);

  // Proxy or error entries — no data available
  if (isProxy || isError) {
    const msg = isError
      ? 'Data unavailable — will retry at next refresh'
      : 'No data available — alternative source pending';
    return (
      <div className="card" style={{padding:'8px 10px', overflow:'hidden'}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5}}>
          <div style={{cursor:'pointer'}} onClick={() => onOpen && onOpen(asset)}>
            <span style={{fontSize:11, fontWeight:700, color:'var(--amber-ink)'}}>{label}</span>
          </div>
          <span style={{fontSize:11, fontWeight:700, padding:'1px 5px', borderRadius:3,
            background:'rgba(100,100,100,.1)', color:'var(--ink-3)'}}>N/A</span>
        </div>
        <div style={{height:chartHeight, display:'flex', alignItems:'center', justifyContent:'center',
          color:'var(--ink-3)', fontSize:11, borderRadius:3, background:'var(--surface-2)'}}>
          {msg}
        </div>
        <div style={{fontSize:11, color:'var(--ink-3)', marginTop:5}}>
          yfinance does not carry this instrument
        </div>
      </div>
    );
  }

  const { signal, last, ret1M } = stats;
  const sigColor = signal === 'Accelerating' ? 'var(--up-field-ink)'
                 : signal === 'Decelerating' ? 'var(--down-field-ink)' : 'var(--amber-ink)';
  const sigBg    = signal === 'Accelerating' ? 'rgba(26,106,26,.1)'
                 : signal === 'Decelerating' ? 'rgba(196,26,26,.1)' : 'rgba(200,146,42,.1)';

  return (
    <div className="card" style={{padding:'8px 10px', overflow:'hidden'}}>
      {/* Chart header */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5}}>
        <div style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}
          onClick={() => onOpen && onOpen(asset)}>
          {rank != null && (
            <span style={{
              fontSize:15, fontWeight:800, width:26, height:26, borderRadius:'50%',
              display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              ...(rank===1 ? {background:'rgba(245,158,11,.15)', color:'#d97706'} :
                  rank===2 ? {background:'rgba(59,130,246,.12)',  color:'#2563eb'} :
                  rank===3 ? {background:'rgba(249,115,22,.12)',  color:'#ea580c'} :
                             {background:'rgba(148,163,184,.12)', color:'#64748b'}),
            }}>
              {rank}
            </span>
          )}
          <span style={{fontSize:11, fontWeight:700, color:'var(--amber-ink)'}}>{label}</span>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:4}}>
          {building && (
            <span title="MA window still accumulating — fewer than 20 bars" style={{
              fontSize:9, fontWeight:600, padding:'1px 4px', borderRadius:3,
              background:'rgba(200,146,42,.15)', color:'var(--amber-ink)', letterSpacing:'.04em',
            }}>BUILDING</span>
          )}
          <CompoundPill strength={_rsStrength(last)} momentum={signal}/>
        </div>
      </div>
      {/* Canvas */}
      <div style={{height:chartHeight, position:'relative', cursor:'crosshair'}}
        onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        <canvas ref={canvasRef} style={{width:'100%', height:'100%', display:'block'}}/>
      </div>
      {/* Footer stats */}
      <div style={{display:'flex', gap:10, marginTop:5, fontSize:11, color:'var(--ink-3)'}}>
        <span>RS <span style={{fontFamily:'var(--font-data)', color:'var(--ink)', fontWeight:600}}>
          {last.toFixed(3)}
        </span></span>
        <span>1M <span style={{fontFamily:'var(--font-data)',
          color:ret1M>=0?'var(--up-field-ink)':'var(--down-field-ink)', fontWeight:600}}>
          {ret1M>=0?'+':''}{ret1M.toFixed(1)}%
        </span></span>
      </div>
    </div>
  );
}

// ── Section 1 — Asset RS Card ─────────────────────────────────────────────────
function RSCard({ asset, stats, onOpen, isProxy }) {
  // Proxy entries (G-Sec etc.) — no yfinance data
  if (isProxy) {
    return (
      <div className="card" style={{padding:'8px 10px', borderLeft:'2px solid var(--border3,var(--hair))', opacity:.75}}>
        <div style={{fontSize:11, fontWeight:700, color:'var(--amber-ink)', marginBottom:4,
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{asset.name}</div>
        <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:4}}>
          <span style={{fontWeight:700, fontSize:13, color:'var(--ink-3)'}}>N/A</span>
          <span style={{fontSize:11, padding:'1px 5px', borderRadius:3, fontWeight:600,
            background:'rgba(100,100,100,.1)', color:'var(--ink-3)'}}>No data</span>
        </div>
        <div style={{fontSize:11, color:'var(--ink-3)'}}>yfinance does not carry this instrument</div>
      </div>
    );
  }

  const { signal, last, ret1W, ret1M, ret3M, spark } = stats;
  const sigColor = signal === 'Accelerating' ? 'var(--up-field-ink)'
                 : signal === 'Decelerating' ? 'var(--down-field-ink)' : 'var(--amber-ink)';
  const sparkMin = Math.min(...spark), sparkMax = Math.max(...spark);
  const sparkH = 24, sparkW = 60;

  // Mini inline sparkline via SVG
  const pts = spark.map((v, i) => {
    const x = (i / (spark.length - 1)) * sparkW;
    const y = sparkH - ((v - sparkMin) / (sparkMax - sparkMin || 0.001)) * sparkH;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="card" style={{padding:'8px 10px', borderLeft:`2px solid ${sigColor}`, cursor:'pointer'}}
      onClick={() => onOpen && onOpen(asset)}>
      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6}}>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:11, fontWeight:600, color:'var(--amber-ink)', marginBottom:2,
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{asset.name}</div>
          <div style={{display:'flex', alignItems:'center', gap:5, marginBottom:4}}>
            <span style={{fontFamily:'var(--font-data)', fontWeight:700, fontSize:13,
              color:'var(--ink)'}}>{last.toFixed(3)}</span>
            <span style={{fontSize:11, padding:'1px 5px', borderRadius:3, fontWeight:600,
              background:signal==='Accelerating'?'rgba(26,106,26,.1)':signal==='Decelerating'?'rgba(196,26,26,.1)':'rgba(200,146,42,.1)',
              color:sigColor}}>
              {signal}
            </span>
          </div>
          <div style={{display:'flex', gap:8, fontSize:11}}>
            {[['1W', ret1W], ['1M', ret1M], ['3M', ret3M]].map(([l, v]) => (
              <span key={l} style={{color:'var(--ink-3)'}}>
                {l} <span style={{fontFamily:'var(--font-data)', fontWeight:600,
                  color: v != null ? (v>=0?'var(--up-field-ink)':'var(--down-field-ink)') : 'var(--ink-3)'}}>
                  {v != null ? `${v>=0?'+':''}${v.toFixed(1)}%` : '—'}
                </span>
              </span>
            ))}
          </div>
        </div>
        <svg width={sparkW} height={sparkH} style={{flexShrink:0, marginTop:2}}>
          <polyline points={pts} fill="none"
            stroke={stats.aboveMA ? '#3f6b4d' /* --up-field-ink */ : '#8f4a44' /* --down-field-ink */} strokeWidth="1.5"
            strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
}

// ── RS Bar Chart — all assets vs NIFTY, grouped by signal ────────────────────
function RSBarChart({ data, onOpen }) {
  const isDark = document.documentElement.classList.contains('dark');
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(e => setContainerW(e[0].contentRect.width));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const valid = useMemo(() =>
    data.filter(d => !d.isProxy && !d.isError && d.stats?.last != null),
    [data]
  );

  const sorted = useMemo(() => [
    ...valid.filter(d => d.stats.signal === 'Accelerating').sort((a, b) => b.stats.last - a.stats.last),
    ...valid.filter(d => d.stats.signal === 'Flat').sort((a, b) => b.stats.last - a.stats.last),
    ...valid.filter(d => d.stats.signal === 'Decelerating').sort((a, b) => b.stats.last - a.stats.last),
  ], [valid]);

  if (sorted.length === 0) return null;

  const outCount  = sorted.filter(d => d.stats.signal === 'Accelerating').length;
  const neutCount = sorted.filter(d => d.stats.signal === 'Flat').length;
  const underCount = sorted.length - outCount - neutCount;

  const BAR_W   = 14;
  const BAR_GAP = 10;
  const CHART_H = 220;  // height of the bar drawing area
  const TOP_PAD = 18;   // space above chart for section labels
  const BOT_PAD = 70;   // space below chart for angled names
  const SVG_H   = TOP_PAD + CHART_H + BOT_PAD;
  const minW    = sorted.length * (BAR_W + BAR_GAP) + BAR_GAP;
  const totalW  = Math.max(minW, containerW > 0 ? containerW - 2 : minW);

  const ratios  = sorted.map(d => d.stats.last);
  const maxUp   = Math.max(0.003, ...ratios.map(r => Math.max(0, r - 1.0)));
  const maxDn   = Math.max(0.003, ...ratios.map(r => Math.max(0, 1.0 - r)));
  const totalRange = maxUp + maxDn;

  const zeroY  = TOP_PAD + (maxUp / totalRange) * CHART_H;
  const pxPer  = CHART_H / totalRange;

  // Each bar gets an equal slice of totalW; bars are centered within their slot
  const stepW    = totalW / sorted.length;
  const outEndX  = outCount * stepW;
  const neutEndX = (outCount + neutCount) * stepW;

  const textFill   = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
  const zeroFill   = isDark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)';

  return (
    <div ref={containerRef} style={{overflowX:'auto', marginBottom:16, borderRadius:4,
      border:'.5px solid var(--hair)', background:'var(--surface)'}}>
      <svg width={totalW} height={SVG_H} style={{display:'block'}}>

        {/* ── Section background fills ── */}
        {outCount > 0 && (
          <rect x={0} y={0} width={outEndX} height={TOP_PAD + CHART_H}
            fill="rgba(26,106,26,.055)"/>
        )}
        {neutCount > 0 && (
          <rect x={outEndX} y={0} width={neutEndX - outEndX} height={TOP_PAD + CHART_H}
            fill="rgba(200,146,42,.055)"/>
        )}
        {underCount > 0 && (
          <rect x={neutEndX} y={0} width={totalW - neutEndX} height={TOP_PAD + CHART_H}
            fill="rgba(196,26,26,.055)"/>
        )}

        {/* ── Section header labels ── */}
        {outCount > 0 && (
          <text x={outEndX / 2} y={10} fontSize={11} textAnchor="middle"
            fill="rgba(26,106,26,.75)" fontFamily="IBM Plex Sans,system-ui,sans-serif" fontWeight={700}
            style={{letterSpacing:'0.07em', textTransform:'uppercase'}}>
            ACCELERATING ({outCount})
          </text>
        )}
        {neutCount > 0 && (
          <text x={(outEndX + neutEndX) / 2} y={10} fontSize={11} textAnchor="middle"
            fill="rgba(200,146,42,.75)" fontFamily="IBM Plex Sans,system-ui,sans-serif" fontWeight={700}>
            FLAT ({neutCount})
          </text>
        )}
        {underCount > 0 && (
          <text x={(neutEndX + totalW) / 2} y={10} fontSize={11} textAnchor="middle"
            fill="rgba(196,26,26,.75)" fontFamily="IBM Plex Sans,system-ui,sans-serif" fontWeight={700}>
            DECELERATING ({underCount})
          </text>
        )}

        {/* ── Zero / 1.0 baseline ── */}
        <line x1={0} y1={zeroY} x2={totalW} y2={zeroY}
          stroke={zeroFill} strokeWidth={1} strokeDasharray="3,2"/>
        <text x={3} y={zeroY - 3} fontSize={11} fill={textFill}
          fontFamily="IBM Plex Mono,monospace">1.0</text>

        {/* ── Section divider lines ── */}
        {outCount > 0 && neutCount + underCount > 0 && (
          <line x1={outEndX} y1={0} x2={outEndX} y2={TOP_PAD + CHART_H}
            stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth={1}/>
        )}
        {neutCount > 0 && underCount > 0 && (
          <line x1={neutEndX} y1={0} x2={neutEndX} y2={TOP_PAD + CHART_H}
            stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'} strokeWidth={1}/>
        )}

        {/* ── Bars + labels ── */}
        {sorted.map((d, i) => {
          const ratio  = d.stats.last;
          const dev    = ratio - 1.0;
          const barH   = Math.max(Math.abs(dev) * pxPer, 1.5);
          const barX   = stepW * i + (stepW - BAR_W) / 2;
          const barY   = dev >= 0 ? zeroY - barH : zeroY;
          const col    = dev >= 0 ? '#3f6b4d' /* --up-field-ink */ : '#d43030';
          const name   = d.asset.name;
          const shortName = name.length > 12 ? name.slice(0, 11) + '…' : name;
          const labelAnchorY = TOP_PAD + CHART_H + 8;

          return (
            <g key={d.asset.id} style={{cursor:'pointer'}}
              onClick={() => onOpen && onOpen(d.asset)}>
              <title>{d.asset.name}: {ratio.toFixed(4)}</title>

              {/* Bar */}
              <rect x={barX} y={barY} width={BAR_W} height={barH}
                fill={col} opacity={0.82} rx={1.5}/>

              {/* Ratio value — above up bars, below down bars */}
              <text
                x={barX + BAR_W / 2}
                y={dev >= 0 ? Math.max(barY - 2, TOP_PAD + 8) : barY + barH + 9}
                fontSize={11}
                textAnchor="middle"
                fill={col}
                fontFamily="IBM Plex Mono,monospace"
                fontWeight={700}>
                {ratio.toFixed(3)}
              </text>

              {/* Asset name — rotated -45° from bottom of chart area */}
              <text
                x={barX + BAR_W / 2}
                y={labelAnchorY}
                fontSize={11}
                textAnchor="end"
                fill={textFill}
                fontFamily="IBM Plex Sans,system-ui,sans-serif"
                transform={`rotate(-45, ${barX + BAR_W / 2}, ${labelAnchorY})`}>
                {shortName}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── RS Tab View — Heatmap / Scatter Snapshot / Leaderboard ──────────────────
// Replaces the flat bar chart with three tabbed visualisations.
// All three share: same data source (allData), same category filter pills,
// same click-to-open-panel handler.

// Signal colour palette shared across all three views
const _TV_SIG = {
  Accelerating:   { fg:'#3f6b4d' /* --up-field-ink */, bg:'rgba(26,138,58,.12)', border:'#2a8a2a' },
  Flat:         { fg:'#c8922a', bg:'rgba(200,146,42,.12)', border:'#c8922a' },
  Decelerating: { fg:'#8f4a44' /* --down-field-ink */, bg:'rgba(196,26,26,.12)',  border:'#8f4a44' /* --down-field-ink */ },
};

// ── Optimal column count: landscape cards, minimal empty last-row cells ───────
// Prefers card width > card height (horizontal tiles like dashboards).
// Among all landscape candidates, picks fewest empty cells; ties → more columns.
function _optimalCols(n, containerW, containerH, gap = 5, minCardW = 90, maxCardW = 220) {
  if (n <= 0 || containerW <= 0) return 1;
  const maxCols = Math.max(1, Math.floor((containerW + gap) / (minCardW + gap)));
  const minCols = Math.max(1, Math.floor((containerW + gap) / (maxCardW + gap)));
  if (n <= maxCols) return n;

  let bestCols = maxCols, bestEmpty = maxCols, bestIsLandscape = false;
  for (let c = minCols; c <= maxCols; c++) {
    const rows     = Math.ceil(n / c);
    const rem      = n % c;
    const empty    = rem === 0 ? 0 : c - rem;
    const cardW    = (containerW - (c - 1) * gap) / c;
    const cardH    = containerH > 0 ? (containerH - (rows - 1) * gap) / rows : cardW;
    const landscape = cardW >= cardH;  // width ≥ height = preferred

    const better =
      // Landscape beats portrait always
      (landscape && !bestIsLandscape) ||
      // Same orientation: fewer empty wins; tie → more columns
      (landscape === bestIsLandscape && (empty < bestEmpty || (empty === bestEmpty && c > bestCols)));

    if (better) { bestCols = c; bestEmpty = empty; bestIsLandscape = landscape; }
  }
  return bestCols;
}

// ── Heatmap tile grid ─────────────────────────────────────────────────────────
function RSHeatmap({ items, onOpen, maxDev }) {
  const containerRef = useRef(null);
  const [cols, setCols] = useState(10);

  useEffect(() => {
    if (!containerRef.current) return;
    const compute = () => {
      const w = containerRef.current.offsetWidth;
      const h = containerRef.current.offsetHeight;
      setCols(_optimalCols(items.length, w, h));
    };
    compute();
    const ob = new ResizeObserver(compute);
    ob.observe(containerRef.current);
    return () => ob.disconnect();
  }, [items.length]);

  return (
    <div ref={containerRef} style={{ display:'grid', height:'100%',
      gridTemplateColumns:`repeat(${cols}, 1fr)`, gridAutoRows:'1fr', gap:5 }}>
      {items.map(d => {
        const isBenchmark = d.asset.id === 'nifty50';
        const { last, signal, ret1M } = d.stats;
        const dev       = last - 1.0;
        const intensity = Math.min(Math.abs(dev) / Math.max(maxDev, 0.25), 1);
        const alpha     = 0.07 + intensity * 0.56;
        const tileBg    = isBenchmark
          ? 'var(--surface-2)'
          : dev >= 0
            ? `rgba(26,138,58,${alpha.toFixed(2)})`
            : `rgba(196,48,48,${alpha.toFixed(2)})`;
        const tc = isBenchmark ? 'var(--ink-3)'
          : intensity > 0.55
            ? (dev >= 0 ? 'rgba(4,52,44,.95)' : 'rgba(80,19,19,.95)')
            : 'var(--ink)';
        const borderCol = isBenchmark
          ? 'var(--hair-strong)'
          : (_TV_SIG[signal] || _TV_SIG.Neutral).border;
        const pct = dev * 100;
        const name = d.asset.name;

        return (
          <div key={d.asset.id} onClick={() => onOpen && onOpen(d.asset)}
            style={{ background:tileBg, border:`2.5px solid ${borderCol}`,
              borderRadius:6, padding:'7px 5px', textAlign:'center',
              cursor:'pointer', minHeight:54,
              display:'flex', flexDirection:'column', alignItems:'center',
              justifyContent:'center', gap:2,
              opacity: isBenchmark ? 0.6 : 1 }}>
            <div style={{ fontSize:11, fontWeight:500, color:tc, lineHeight:1.2,
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              width:'100%', textAlign:'center' }} title={name}>
              {name.length > 13 ? name.slice(0,12) + '…' : name}
            </div>
            {isBenchmark ? (
              <div style={{ fontSize:10, color:'var(--ink-3)', fontStyle:'italic' }}>benchmark</div>
            ) : (
              <>
                <div style={{ fontFamily:'var(--font-data)', fontSize:12,
                  fontWeight:700, color:tc }}>
                  {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
                </div>
                {ret1M != null && (
                  <div style={{ fontSize:11, color:tc, opacity:.75 }}>
                    {ret1M >= 0 ? '↑' : '↓'} {Math.abs(ret1M).toFixed(1)}%
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Scatter snapshot — RS ratio (X) × RS momentum 1M (Y) ────────────────────
function RSScatterSnap({ items, onOpen }) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(0);
  const [tooltip,    setTooltip]    = useState(null);
  const dotPosRef = useRef([]);   // mutable ref; avoids stale closure in handlers

  const valid = useMemo(() =>
    items.filter(d => d.stats.last != null && d.stats.ret1M != null),
    [items]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(e => setContainerW(Math.floor(e[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !containerW || valid.length === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const W = containerW, H = 460;
    canvas.width  = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const isDark = document.documentElement.classList.contains('dark');
    const PAD = { l:52, r:20, t:22, b:42 };
    const cW = W - PAD.l - PAD.r, cH = H - PAD.t - PAD.b;

    const xs = valid.map(d => d.stats.last);
    const ys = valid.map(d => d.stats.ret1M);
    const xSpan = Math.max(...xs) - Math.min(...xs) || 0.1;
    const ySpan = Math.max(...ys) - Math.min(...ys) || 4;
    const xMin = Math.min(...xs) - xSpan * 0.08;
    const xMax = Math.max(...xs) + xSpan * 0.08;
    const yMin = Math.min(...ys) - ySpan * 0.08;
    const yMax = Math.max(...ys) + ySpan * 0.08;

    const toX = v => PAD.l + (v - xMin) / (xMax - xMin) * cW;
    const toY = v => PAD.t + (1 - (v - yMin) / (yMax - yMin)) * cH;
    // Clamp zero-lines inside chart area
    const x0 = Math.max(PAD.l + 1, Math.min(PAD.l + cW - 1, toX(1.0)));
    const y0 = Math.max(PAD.t + 1, Math.min(PAD.t + cH - 1, toY(0)));

    // Quadrant fills
    [ [x0, PAD.t,  PAD.l+cW-x0, y0-PAD.t,          'rgba(26,138,58,.07)' ],   // leaders
      [PAD.l, PAD.t, x0-PAD.l,  y0-PAD.t,           'rgba(200,146,42,.05)'],   // emerging
      [x0, y0,    PAD.l+cW-x0,  PAD.t+cH-y0,        'rgba(200,146,42,.05)'],   // fading
      [PAD.l, y0,  x0-PAD.l,   PAD.t+cH-y0,         'rgba(196,26,26,.07)' ],   // avoid
    ].forEach(([x,y,w,h,fill]) => { if (w>0&&h>0) { ctx.fillStyle=fill; ctx.fillRect(x,y,w,h); } });

    // Zero-lines
    ctx.strokeStyle = isDark ? 'rgba(255,255,255,.28)' : 'rgba(0,0,0,.22)';
    ctx.lineWidth = 1; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(x0, PAD.t); ctx.lineTo(x0, PAD.t+cH); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(PAD.l, y0); ctx.lineTo(PAD.l+cW, y0); ctx.stroke();
    ctx.setLineDash([]);

    const textFill = isDark ? 'rgba(255,255,255,.38)' : 'rgba(0,0,0,.42)';

    // X ticks
    ctx.fillStyle = textFill; ctx.font = '9px "IBM Plex Mono",monospace'; ctx.textAlign = 'center';
    const xStep = xSpan > 0.5 ? 0.1 : 0.05;
    for (let v = Math.ceil(xMin/xStep)*xStep; v <= xMax+0.001; v += xStep) {
      const px = toX(v); if (px < PAD.l-4 || px > PAD.l+cW+4) continue;
      const lbl = ((v-1)*100).toFixed(0);
      ctx.fillText((parseFloat(lbl)>=0?'+':'')+lbl+'%', px, PAD.t+cH+13);
    }

    // Y ticks
    ctx.textAlign = 'right';
    const yStep = (yMax-yMin) > 20 ? 5 : (yMax-yMin) > 10 ? 2 : 1;
    for (let v = Math.ceil(yMin/yStep)*yStep; v <= yMax+0.001; v += yStep) {
      const py = toY(v); if (py < PAD.t-4 || py > PAD.t+cH+4) continue;
      ctx.fillText((v>=0?'+':'')+v+'%', PAD.l-4, py+3);
    }

    // Quadrant labels
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif';
    const qA = isDark ? .3 : .28; const qP = 5;
    ctx.fillStyle = `rgba(26,138,58,${qA})`; ctx.textAlign = 'left';
    ctx.fillText('Leaders ▲', x0+qP, PAD.t+12);
    ctx.fillStyle = `rgba(200,146,42,${qA})`; ctx.textAlign = 'right';
    ctx.fillText('Emerging ▲', x0-qP, PAD.t+12); ctx.textAlign = 'left';
    ctx.fillText('Fading ▼',   x0+qP, PAD.t+cH-qP);
    ctx.fillStyle = `rgba(196,26,26,${qA})`; ctx.textAlign = 'right';
    ctx.fillText('Avoid ▼',    x0-qP, PAD.t+cH-qP);

    // Axis titles
    ctx.fillStyle = isDark ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.22)';
    ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('← weaker vs NIFTY   RS Ratio   stronger vs NIFTY →', PAD.l+cW/2, H-5);
    ctx.save(); ctx.translate(11, PAD.t+cH/2); ctx.rotate(-Math.PI/2);
    ctx.fillText('RS Momentum (1M) →', 0, 0); ctx.restore();

    // Dots + labels
    const positions = [];
    valid.forEach(d => {
      const px = toX(d.stats.last), py = toY(d.stats.ret1M);
      const col = (_TV_SIG[d.stats.signal] || _TV_SIG.Neutral).fg;
      ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI*2);
      ctx.fillStyle = col; ctx.fill();
      ctx.strokeStyle = isDark ? 'rgba(0,0,0,.5)' : 'rgba(255,255,255,.8)';
      ctx.lineWidth = 1.5; ctx.stroke();
      const sn = d.asset.name.length > 9 ? d.asset.name.slice(0,8)+'…' : d.asset.name;
      ctx.fillStyle = isDark ? 'rgba(255,255,255,.48)' : 'rgba(0,0,0,.48)';
      ctx.font = '9px "IBM Plex Sans",system-ui,sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(sn, px+8, py+3);
      positions.push({ px, py, d });
    });
    dotPosRef.current = positions;
  }, [valid, containerW]);

  const handleMouseMove = useCallback(e => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const hit = dotPosRef.current.find(p => Math.hypot(p.px-mx, p.py-my) < 9);
    setTooltip(hit ? { x:mx, y:my, d:hit.d } : null);
  }, []);

  return (
    <div ref={containerRef} style={{ position:'relative' }}>
      <canvas ref={canvasRef} style={{ display:'block', width:'100%', cursor:'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onClick={e => {
          const canvas = canvasRef.current; if (!canvas) return;
          const rect = canvas.getBoundingClientRect();
          const mx = e.clientX - rect.left, my = e.clientY - rect.top;
          const hit = dotPosRef.current.find(p => Math.hypot(p.px-mx, p.py-my) < 9);
          if (hit) onOpen && onOpen(hit.d.asset);
        }}/>
      {tooltip && (
        <div style={{ position:'absolute', left:tooltip.x+12, top:tooltip.y-8,
          background:'var(--surface)', border:'.5px solid var(--hair)', borderRadius:4,
          padding:'5px 9px', fontSize:11, color:'var(--ink)', pointerEvents:'none', zIndex:10 }}>
          <div style={{ fontWeight:700 }}>{tooltip.d.asset.name}</div>
          <div style={{ fontFamily:'var(--font-data)', fontSize:11, color:'var(--ink-3)', marginTop:2 }}>
            RS: {((tooltip.d.stats.last-1)*100 >= 0 ? '+' : '')}{((tooltip.d.stats.last-1)*100).toFixed(1)}%
            &nbsp;·&nbsp;Mom: {tooltip.d.stats.ret1M >= 0 ? '+' : ''}{tooltip.d.stats.ret1M.toFixed(1)}%
          </div>
          <div style={{ fontSize:11, fontWeight:700, marginTop:2,
            color:(_TV_SIG[tooltip.d.stats.signal]||_TV_SIG.Neutral).fg }}>
            {tooltip.d.stats.signal}
          </div>
        </div>
      )}
      <div style={{ marginTop:6, fontSize:11, color:'var(--ink-3)', lineHeight:1.5 }}>
        X = 20W RS ratio vs NIFTY 50 (daily, normalised to 1.0 at start of window) ·
        Y = RS momentum: % change in ratio over last 21 sessions (1M) ·
        Dot colour = MA signal (same ±0.5% band as other views) ·
        Note: uses raw daily data — not the JDK-smoothed weekly calculation used in the Sector Rotation tab
      </div>
    </div>
  );
}

// ── Leaderboard — ranked list with diverging bar + signal pill ───────────────
function RSLeaderboard({ items, onOpen, maxDev }) {
  return (
    <div style={{ maxHeight:560, overflowY:'auto' }}>
      {items.map((d, i) => {
        const isBenchmark = d.asset.id === 'nifty50';
        const { last, signal } = d.stats;
        const pct    = (last - 1) * 100;
        const isPos  = pct >= 0;
        const barW   = Math.min(Math.abs(pct) / Math.max(maxDev * 100, 1) * 50, 50);
        const sc     = isBenchmark ? { border:'var(--hair-strong)' } : (_TV_SIG[signal] || _TV_SIG.Flat);
        const momentumA = isBenchmark ? '—' : signal === 'Accelerating' ? '↗' : signal === 'Decelerating' ? '↘' : '→';
        const momentumC = isBenchmark ? 'var(--ink-3)' : signal === 'Accelerating' ? 'var(--up-field-ink)' : signal === 'Decelerating' ? 'var(--down-field-ink)' : 'var(--amber-ink)';

        return (
          <div key={d.asset.id} onClick={() => onOpen && onOpen(d.asset)}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 8px 4px 10px',
              borderBottom:'.5px solid var(--hair)', borderLeft:`3px solid ${sc.border}`,
              cursor:'pointer', opacity: isBenchmark ? 0.55 : 1,
              background: isBenchmark ? 'var(--surface-2)' : 'transparent' }}>
            {/* Rank / benchmark marker */}
            <span style={{ fontSize:11, color:'var(--ink-3)', width:18, textAlign:'right',
              flexShrink:0, fontFamily:'var(--font-data)' }}>{isBenchmark ? '—' : i+1}</span>
            {/* Name */}
            <span style={{ fontSize:11, color: isBenchmark ? 'var(--ink-3)' : 'var(--ink)', width:112, flexShrink:0,
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontStyle: isBenchmark ? 'italic' : 'normal' }}
              title={d.asset.name}>{d.asset.name}</span>
            {/* Category */}
            <span style={{ fontSize:11, color:'var(--ink-3)', width:66, flexShrink:0,
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d.asset.cat}</span>
            {/* Diverging bar */}
            <div style={{ flex:1, position:'relative', height:10,
              background:'rgba(128,128,128,.1)', borderRadius:3 }}>
              <div style={{ position:'absolute', left:'50%', top:0, bottom:0,
                width:1, background:'rgba(128,128,128,.3)' }}/>
              {!isBenchmark && (
                <div style={{ position:'absolute', top:0, bottom:0, borderRadius:3,
                  [isPos ? 'left' : 'right']: '50%',
                  width:`${barW}%`,
                  background: isPos ? '#3f6b4d' : '#8f4a44' }}/>
              )}
            </div>
            {/* % value */}
            <span style={{ fontSize:11, fontFamily:'var(--font-data)', fontWeight:600,
              width:52, textAlign:'right', flexShrink:0,
              color: isBenchmark ? 'var(--ink-3)' : isPos ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
              {isBenchmark ? 'benchmark' : `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
            </span>
            {/* Momentum arrow */}
            <span style={{ fontSize:14, width:16, textAlign:'center', flexShrink:0,
              color:momentumC, fontWeight:700 }}>{momentumA}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── RSTabView — outer shell with tab bar + shared filter pills ────────────────
const TV_TABS = [
  { id:'heatmap',  label:'Heatmap' },
  { id:'leaders',  label:'Leaderboard' },
];

function RSTabView({ data, onOpen }) {
  const [tab,       setTab]      = useState('heatmap');
  const [filter,    setFilter]   = useState('All');

  const valid = useMemo(() =>
    data.filter(d => !d.isProxy && !d.isError && d.stats?.last != null),
    [data]
  );

  const filtered = useMemo(() =>
    valid.filter(d => matchFilter(d.asset.cat, filter)),
    [valid, filter]
  );

  // Sorted ratio-descending for heatmap + leaderboard; benchmark pinned to middle
  const ratioSorted = useMemo(() => {
    const benchmark = filtered.filter(d => d.asset.id === 'nifty50');
    const rest = [...filtered.filter(d => d.asset.id !== 'nifty50')].sort((a, b) => b.stats.last - a.stats.last);
    // Insert benchmark after the last item >= 1.0 so it sits near the zero line
    const insertAt = rest.findIndex(d => d.stats.last < 1.0);
    if (insertAt === -1) return [...rest, ...benchmark];
    return [...rest.slice(0, insertAt), ...benchmark, ...rest.slice(insertAt)];
  }, [filtered]);

  const maxDev = useMemo(() =>
    Math.max(0.01, ...filtered.filter(d => d.asset.id !== 'nifty50').map(d => Math.abs(d.stats.last - 1))),
    [filtered]
  );

  // Momentum + strength counts exclude the benchmark (it has no RS vs itself)
  const nonBenchmark = filtered.filter(d => d.asset.id !== 'nifty50');
  const outCount   = nonBenchmark.filter(d => d.stats.signal === 'Accelerating').length;
  const neutCount  = nonBenchmark.filter(d => d.stats.signal === 'Flat').length;
  const underCount = nonBenchmark.length - outCount - neutCount;
  const strongCount = nonBenchmark.filter(d => _rsStrength(d.stats.last) === 'Strong').length;
  const equalCount  = nonBenchmark.filter(d => _rsStrength(d.stats.last) === 'Equal').length;
  const weakCount   = nonBenchmark.length - strongCount - equalCount;

  return (
    <div style={{ marginBottom:16, borderRadius:4, border:'.5px solid var(--hair)',
      background:'var(--surface)' }}>

      {/* ── Header: tab buttons + signal counts + filter pills ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:6, padding:'7px 10px',
        borderBottom:'.5px solid var(--hair)' }}>

        {/* Left: tab buttons */}
        <div style={{ display:'flex', gap:3, alignItems:'center' }}>
          {TV_TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ fontSize:11, padding:'3px 10px', borderRadius:10, cursor:'pointer',
                border:'.5px solid var(--hair-strong)', fontWeight:tab===t.id ? 700 : 400,
                background:tab===t.id ? 'var(--amber-ink)' : 'transparent',
                color:tab===t.id ? '#fff' : 'var(--ink-3)', transition:'all .15s' }}>
              {t.label}
            </button>
          ))}
          {/* (i) info button */}
          <div style={{ marginLeft:4 }}>
            <InfoBtn>Heatmap: fill = RS Strength, border = Momentum. Leaderboard: bar length = Strength, arrow = Momentum direction.</InfoBtn>
          </div>
        </div>

        {/* Middle: signal counts (momentum) + strength counts */}
        <div style={{ display:'flex', gap:10, fontSize:11, alignItems:'center' }}>
          <span style={{ color:'var(--up-field-ink)', fontWeight:600 }}>↗ {outCount}</span>
          <span style={{ color:'var(--amber-ink)', fontWeight:600 }}>→ {neutCount}</span>
          <span style={{ color:'var(--down-field-ink)', fontWeight:600 }}>↘ {underCount}</span>
          <span style={{ color:'var(--ink-3)', margin:'0 2px' }}>·</span>
          <span style={{ color:'var(--up-field-ink)', fontWeight:600 }}>S {strongCount}</span>
          <span style={{ color:'var(--amber-ink)', fontWeight:600 }}>E {equalCount}</span>
          <span style={{ color:'var(--down-field-ink)', fontWeight:600 }}>W {weakCount}</span>
        </div>

        {/* Right: category filter pills */}
        <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
          {CHART_FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ fontSize:11, padding:'2px 8px', borderRadius:10, cursor:'pointer',
                border:'.5px solid var(--hair-strong)', fontWeight:filter===f ? 700 : 400,
                background:filter===f ? 'var(--surface-2)' : 'transparent',
                color:filter===f ? 'var(--ink)' : 'var(--ink-3)', transition:'all .15s' }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ padding:'10px', height:460, boxSizing:'border-box',
        overflowY: tab === 'leaders' ? 'auto' : 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding:'24px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
            No data for selected filter
          </div>
        ) : tab === 'heatmap' ? (
          <RSHeatmap items={ratioSorted} onOpen={onOpen} maxDev={maxDev}/>
        ) : (
          <RSLeaderboard items={ratioSorted} onOpen={onOpen} maxDev={maxDev}/>
        )}
      </div>

      {/* ── Legend strip ── */}
      <div style={{ padding:'5px 10px', borderTop:'.5px solid var(--hair)',
        display:'flex', gap:16, fontSize:11, color:'var(--ink-3)', flexWrap:'wrap' }}>
        <span>Tile/bar colour = 20W RS ratio magnitude</span>
        <span style={{ color:_TV_SIG.Accelerating.border }}>■ Accelerating</span>
        <span style={{ color:_TV_SIG.Flat.border }}>■ Flat</span>
        <span style={{ color:_TV_SIG.Decelerating.border }}>■ Decelerating</span>
        <span style={{ marginLeft:'auto', opacity:.6 }}>border/colour = MA signal</span>
      </div>
    </div>
  );
}

// ── Section 3 — RS Table ──────────────────────────────────────────────────────
const COL_SIGNAL = {
  Accelerating:   { bg:'rgba(26,106,26,.10)',  c:'var(--up-field-ink)' },
  Decelerating: { bg:'rgba(196,26,26,.10)',  c:'var(--down-field-ink)' },
  Flat:         { bg:'rgba(200,146,42,.10)', c:'var(--amber-ink)' },
};

// Column widths — tableLayout:'fixed'.
// All columns have explicit px widths. Signal column has no width → fills remaining space.
// minWidth:700 on table prevents signal from collapsing below ~150px on narrow containers.
const SPARK_W   = 48;
const NAME_W    = 160;  // asset/stock name — fixed (was fill-all-remaining, looked too wide)
const CAT_W     = 72;   // "Category" / "Sector" — capped to fit short labels
const NUM_W     = 52;   // RS 1W/1M/3M + Ratio numeric columns
const TREND_W   = 72;
const SIG_W     = 100;  // minimum; signal col fills remaining space on wide screens
const NUM_STYLE = { whiteSpace:'nowrap', padding:'5px 6px' }; // numeric td base

function RSTable({ data, onOpen }) {
  const [sort,   setSort]   = useState({ col:'ret1M', dir:-1 });
  const [search, setSearch] = useState('');
  const handleSort = col => setSort(s => s.col===col ? {...s, dir:-s.dir} : {col, dir:-1});

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? data.filter(d =>
          (d.asset.name || '').toLowerCase().includes(q) ||
          (d.asset.cat  || '').toLowerCase().includes(q))
      : data;
    return [...filtered].sort((a, b) => {
      // String columns
      if (['name','cat','signal'].includes(sort.col)) {
        const av = (sort.col==='name' ? a.asset.name : sort.col==='cat' ? a.asset.cat : a.stats[sort.col]) || '';
        const bv = (sort.col==='name' ? b.asset.name : sort.col==='cat' ? b.asset.cat : b.stats[sort.col]) || '';
        return av.localeCompare(bv) * sort.dir;
      }
      const av = a.stats[sort.col] ?? 0;
      const bv = b.stats[sort.col] ?? 0;
      return (av - bv) * sort.dir;
    });
  }, [data, sort, search]);

  const SH = ({ col, children, right }) => {
    const active = sort.col === col;
    return (
      <th onClick={() => handleSort(col)}
        style={{textAlign:right?'right':'left', padding:'5px 8px', cursor:'pointer',
          userSelect:'none', color:'var(--ink-3)', fontSize:11, fontWeight:700,
          textTransform:'uppercase', letterSpacing:'.04em', background:'var(--surface-2)',
          borderBottom:'.5px solid var(--hair)', whiteSpace:'nowrap',
          position:'sticky', top:0, zIndex:2}}>
        {children}
        <span style={{marginLeft:2, opacity:active?1:.3, fontSize:11,
          color:active?'var(--amber-ink)':'var(--ink-3)'}}>
          {active?(sort.dir>0?'↑':'↓'):'↕'}
        </span>
      </th>
    );
  };

  const thSpark = { width:SPARK_W, padding:'5px 4px',
    position:'sticky', top:0, zIndex:2, background:'var(--surface-2)',
    borderBottom:'.5px solid var(--hair)' };

  return (
    <>
      <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:8, flexWrap:'wrap'}}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search asset or category..."
          style={{fontSize:11, padding:'3px 8px', borderRadius:3,
            border:'.5px solid var(--hair-strong)', background:'var(--ibg)',
            color:'var(--ink)', outline:'none', width:180}}/>
        <span style={{fontSize:11, color:'var(--ink-3)'}}>
          {rows.length} of {data.length} assets
        </span>
        {search && (
          <button onClick={() => setSearch('')}
            style={{fontSize:11, padding:'1px 7px', borderRadius:3, cursor:'pointer',
              border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', background:'transparent'}}>
            ✕ Clear
          </button>
        )}
      </div>
    <div className="tw" style={{maxHeight:420, overflowY:'auto'}}>
      <table style={{tableLayout:'fixed', width:'100%', minWidth:700, borderCollapse:'collapse'}}>
        <colgroup>
          <col style={{width:SPARK_W}}/>
          <col style={{width:NAME_W}}/>  {/* name — fixed 160px */}
          <col style={{width:CAT_W}}/>
          <col/><col/><col/>  {/* RS 1W / 1M / 3M — equal share of remaining */}
          <col/>               {/* Ratio */}
          <col/>               {/* Signal */}
        </colgroup>
        <thead>
          <tr>
            <th style={thSpark}/>
            <SH col="name">Asset</SH>
            <SH col="cat">Category</SH>
            <SH col="ret1W"   right>RS 1W</SH>
            <SH col="ret1M"   right>RS 1M</SH>
            <SH col="ret3M"   right>RS 3M</SH>
            <SH col="last"    right>Ratio (20W)</SH>
            <SH col="signal">Signal (1M)</SH>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.isProxy || row.isError) {
              const naMsg = row.isError
                ? 'N/A — data unavailable · will retry at next refresh'
                : 'N/A — no yfinance data · alternative source pending';
              return (
                <tr key={row.asset.id} style={{opacity:.6}}>
                  <td style={{padding:'4px 4px'}}><Spark data={[]} w={SPARK_W-8} h={20}/></td>
                  <td className="tlink thl" style={{padding:'5px 6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                    title={row.asset.name}>{row.asset.name}</td>
                  <td style={{padding:'5px 6px', fontSize:11, color:'var(--ink-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                    title={row.asset.cat}>{row.asset.cat}</td>
                  <td colSpan={4} style={{...NUM_STYLE, fontSize:11, color:'var(--ink-3)',
                    fontStyle:'italic'}}>{naMsg}</td>
                  <td style={{padding:'5px 6px', textAlign:'left'}}>
                    <span style={{fontSize:11, fontWeight:700, padding:'1px 6px', borderRadius:3,
                      background:'rgba(100,100,100,.1)', color:'var(--ink-3)'}}>N/A</span>
                  </td>
                </tr>
              );
            }
            const { ret1W, ret1M, ret3M, last, signal, spark } = row.stats;
            const sparkColor = signal === 'Accelerating' ? 'var(--up-field-ink)'
                             : signal === 'Decelerating' ? 'var(--down-field-ink)' : undefined;
            return (
              <tr key={row.asset.id}>
                <td style={{padding:'4px 4px'}}>
                  <Spark data={spark || []} w={SPARK_W-8} h={20} color={sparkColor}/>
                </td>
                <td className="tlink thl" style={{padding:'5px 6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                  title={row.asset.name}
                  onClick={() => onOpen && onOpen(row.asset)}>{row.asset.name}</td>
                <td style={{padding:'5px 6px', fontSize:11, color:'var(--ink-3)',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                  title={row.asset.cat}>{row.asset.cat}</td>
                {[ret1W, ret1M, ret3M].map((v, j) => (
                  <td key={j} style={{...NUM_STYLE, textAlign:'right', fontSize:11,
                    fontFamily:'var(--font-data)', fontWeight:600,
                    color: v == null ? 'var(--ink-3)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}}>
                    {v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                  </td>
                ))}
                <td style={{...NUM_STYLE, textAlign:'right', fontSize:11,
                  fontFamily:'var(--font-data)', color:'var(--ink)'}}>{last != null ? last.toFixed(3) : '—'}</td>
                <td style={{padding:'5px 6px', textAlign:'left'}}>
                  <div style={{display:'flex', alignItems:'center', gap:4}}>
                    {row.building && (
                      <span title="MA window still accumulating — fewer than 20 bars" style={{
                        fontSize:9, fontWeight:600, padding:'1px 4px', borderRadius:3,
                        background:'rgba(200,146,42,.15)', color:'var(--amber-ink)', letterSpacing:'.04em',
                      }}>BLDG</span>
                    )}
                    {signal != null
                      ? <CompoundPill strength={_rsStrength(last)} momentum={signal}/>
                      : <span style={{color:'var(--ink-3)', fontSize:11}}>—</span>}
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={8} style={{padding:'24px', textAlign:'center',
              color:'var(--ink-3)', fontSize:11}}>
              Asset RS data not available — use Retry above or wait for the next backend refresh.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ── Section 4 — Stock RS Scanner (F&O universe) ──────────────────────────────

function StockRSTable({ rows, onOpen }) {
  const [sort, setSort] = useState({ col:'ret1M', dir:-1 });

  const handleSort = col => setSort(s => s.col===col ? {...s,dir:-s.dir} : {col,dir:-1});

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (['name','sector','signal'].includes(sort.col)) {
        const av = sort.col==='name' ? a.name : sort.col==='sector' ? a.sector : a.stats[sort.col] || '';
        const bv = sort.col==='name' ? b.name : sort.col==='sector' ? b.sector : b.stats[sort.col] || '';
        return av.localeCompare(bv) * sort.dir;
      }
      const av = a.stats[sort.col] ?? 0;
      const bv = b.stats[sort.col] ?? 0;
      return (av - bv) * sort.dir;
    });
  }, [rows, sort]);

  const SH = ({ col, children, right }) => {
    const active = sort.col === col;
    return (
      <th onClick={() => handleSort(col)}
        style={{textAlign:right?'right':'left', padding:'5px 8px', cursor:'pointer',
          userSelect:'none', color:'var(--ink-3)', fontSize:11, fontWeight:700,
          textTransform:'uppercase', letterSpacing:'.04em', position:'sticky', top:0, zIndex:2,
          borderBottom:'.5px solid var(--hair)', whiteSpace:'nowrap', background:'var(--surface-2)'}}>
        {children}
        <span style={{marginLeft:2, opacity:active?1:.3, fontSize:11,
          color:active?'var(--amber-ink)':'var(--ink-3)'}}>
          {active?(sort.dir>0?'↑':'↓'):'↕'}
        </span>
      </th>
    );
  };

  const thSpark = { width:SPARK_W, padding:'5px 4px',
    position:'sticky', top:0, zIndex:2, background:'var(--surface-2)',
    borderBottom:'.5px solid var(--hair)' };

  return (
    <div className="tw" style={{maxHeight:520, overflowY:'auto'}}>
      <table style={{tableLayout:'fixed', width:'100%', minWidth:700, borderCollapse:'collapse'}}>
        <colgroup>
          <col style={{width:SPARK_W}}/>
          <col style={{width:NAME_W}}/>  {/* name — fixed 160px */}
          <col style={{width:CAT_W}}/>
          <col/><col/><col/>  {/* RS 1W / 1M / 3M — equal share of remaining */}
          <col/>               {/* Ratio */}
          <col/>               {/* Signal */}
        </colgroup>
        <thead>
          <tr>
            <th style={thSpark}/>
            <SH col="name">Stock</SH>
            <SH col="sector">Sector</SH>
            <SH col="ret1W"  right>RS 1W</SH>
            <SH col="ret1M"  right>RS 1M</SH>
            <SH col="ret3M"  right>RS 3M</SH>
            <SH col="last"   right>Ratio (20W)</SH>
            <SH col="signal">Signal (1M)</SH>
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => {
            const { ret1W, ret1M, ret3M, last, signal, spark } = row.stats;
            const sparkColor = signal === 'Accelerating' ? 'var(--up-field-ink)'
                             : signal === 'Decelerating' ? 'var(--down-field-ink)' : undefined;
            return (
              <tr key={row.name}>
                <td style={{padding:'4px 4px'}}>
                  <Spark data={spark || []} w={SPARK_W-8} h={20} color={sparkColor}/>
                </td>
                <td className="tlink thl" style={{padding:'5px 6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                  title={row.name}
                  onClick={() => onOpen && onOpen({ name:row.name, sector:row.sector })}>
                  {row.name}
                </td>
                <td style={{padding:'5px 6px', fontSize:11, color:'var(--ink-3)',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                  title={row.sector}>{row.sector}</td>
                {[ret1W, ret1M, ret3M].map((v, j) => (
                  <td key={j} style={{...NUM_STYLE, textAlign:'right', fontSize:11,
                    fontFamily:'var(--font-data)', fontWeight:600,
                    color: v == null ? 'var(--ink-3)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)'}}>
                    {v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                  </td>
                ))}
                <td style={{...NUM_STYLE, textAlign:'right', fontSize:11,
                  fontFamily:'var(--font-data)', color: last != null ? 'var(--ink)' : 'var(--ink-3)'}}>
                  {last != null ? last.toFixed(3) : '—'}
                </td>
                <td style={{padding:'5px 6px', textAlign:'left'}}>
                  <div style={{display:'flex', alignItems:'center', gap:4}}>
                    {row.building && (
                      <span title="MA window still accumulating — fewer than 20 bars" style={{
                        fontSize:9, fontWeight:600, padding:'1px 4px', borderRadius:3,
                        background:'rgba(200,146,42,.15)', color:'var(--amber-ink)', letterSpacing:'.04em',
                      }}>BLDG</span>
                    )}
                    {signal != null
                      ? <CompoundPill strength={_rsStrength(last)} momentum={signal}/>
                      : <span style={{color:'var(--ink-3)', fontSize:11}}>—</span>}
                  </div>
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr><td colSpan={8} style={{padding:'24px', textAlign:'center',
              color:'var(--ink-3)', fontSize:11}}>No stocks match your filter</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
// ── Section 5 — F&O Stock RS Rankings ────────────────────────────────────────

function RankingsTable({ allStockData, onOpen }) {
  // Default sort: Total RS & P Score descending
  const [sort,     setSort]     = useState({ col: 'totalRSP', dir: -1 });
  const [search,   setSearch]   = useState('');
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'table'
  const [expanded, setExpanded] = useState({}); // symbol → boolean (visual expand)

  const handleSort = col =>
    setSort(s => s.col === col ? { ...s, dir: -s.dir } : { col, dir: -1 });

  // ── Assign ordinal ranks for a metric (ascending: rank 1 = worst, rank N = best) ──
  const assignRanks = (rows, getVal) => {
    const eligible = rows.filter(r => getVal(r) != null)
      .sort((a, b) => getVal(a) - getVal(b));
    const map = {};
    eligible.forEach((r, i) => { map[r.name] = i + 1; });
    return { map, total: eligible.length };
  };

  const { ranked, totals } = useMemo(() => {
    const { map: mRS1W,  total: tRS1W  } = assignRanks(allStockData, r => r.stats.ret1W);
    const { map: mRS1M,  total: tRS1M  } = assignRanks(allStockData, r => r.stats.ret1M);
    const { map: mRS3M,  total: tRS3M  } = assignRanks(allStockData, r => r.stats.ret3M);
    const { map: mP1W,   total: tP1W   } = assignRanks(allStockData, r => r.stats.price1W);
    const { map: mP1M,   total: tP1M   } = assignRanks(allStockData, r => r.stats.price1M);
    const { map: mP3M,   total: tP3M   } = assignRanks(allStockData, r => r.stats.price3M);

    const ranked = allStockData.map(r => {
      const rs1w = mRS1W[r.name] ?? null;
      const rs1m = mRS1M[r.name] ?? null;
      const rs3m = mRS3M[r.name] ?? null;
      const p1w  = mP1W[r.name]  ?? null;
      const p1m  = mP1M[r.name]  ?? null;
      const p3m  = mP3M[r.name]  ?? null;
      const totalRS  = (rs1w ?? 0) + (rs1m ?? 0) + (rs3m ?? 0);
      const totalP   = (p1w  ?? 0) + (p1m  ?? 0) + (p3m  ?? 0);
      return {
        name: r.name, sector: r.sector, spark: r.stats.spark, signal: r.stats.signal,
        rs1w, rs1m, rs3m, p1w, p1m, p3m,
        totalRS, totalP, totalRSP: totalRS + totalP,
      };
    });

    return {
      ranked,
      totals: { tRS1W, tRS1M, tRS3M, tP1W, tP1M, tP3M,
                maxRS: tRS1W + tRS1M + tRS3M,
                maxP:  tP1W  + tP1M  + tP3M  },
    };
  }, [allStockData]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? ranked.filter(r => r.name.toLowerCase().includes(q) || r.sector.toLowerCase().includes(q))
      : ranked;
    return [...rows].sort((a, b) => {
      if (sort.col === 'name')   return a.name.localeCompare(b.name) * sort.dir;
      if (sort.col === 'sector') return a.sector.localeCompare(b.sector) * sort.dir;
      return ((a[sort.col] ?? -1) - (b[sort.col] ?? -1)) * sort.dir;
    });
  }, [ranked, sort, search]);

  // Header cell — identical look to StockRSTable SH, optional bg tint for column groups
  const SH = ({ col, children, right, title: tip, bg }) => {
    const active = sort.col === col;
    return (
      <th onClick={() => handleSort(col)} title={tip}
        style={{ textAlign: right ? 'right' : 'left', padding: '5px 8px', cursor: 'pointer',
          userSelect: 'none', color: 'var(--ink-3)', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '.04em', position: 'sticky', top: 0, zIndex: 2,
          borderBottom: '.5px solid var(--hair)', whiteSpace: 'nowrap',
          // Solid base colour makes the sticky header opaque so rows don't bleed through.
          // The optional tint (bg) is layered on top via backgroundImage so it stays visible.
          background: 'var(--surface-2)',
          backgroundImage: bg ? `linear-gradient(${bg}, ${bg})` : 'none' }}>
        {children}
        <span style={{ marginLeft: 2, opacity: active ? 1 : .3, fontSize: 11,
          color: active ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
          {active ? (sort.dir > 0 ? '↑' : '↓') : '↕'}
        </span>
      </th>
    );
  };

  // Color helpers
  const rankCol = (r, t) => {
    if (r == null || t === 0) return 'var(--ink-3)';
    const p = r / t;
    return p >= 0.8 ? 'var(--up-field-ink)' : p <= 0.2 ? 'var(--down-field-ink)' : 'var(--ink)';
  };
  const scoreCol = (v, max) => {
    if (!v || max === 0) return 'var(--ink)';
    const p = v / max;
    return p >= 0.8 ? 'var(--up-field-ink)' : p <= 0.2 ? 'var(--down-field-ink)' : 'var(--ink)';
  };
  const { tRS1W, tRS1M, tRS3M, tP1W, tP1M, tP3M, maxRS, maxP } = totals;
  const maxRSP = maxRS + maxP;

  // Rank cell renderer
  const RankCell = ({ r, t }) => (
    <td style={{ ...NUM_STYLE, textAlign: 'right', fontSize: 11,
      fontWeight: r != null && t > 0 && r / t >= 0.8 ? 700 : 400,
      color: rankCol(r, t) }}>
      {r ?? '—'}
      {r != null && <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 2 }}>/{t}</span>}
    </td>
  );

  const toggleExpand = name => setExpanded(e => ({ ...e, [name]: !e[name] }));

  return (
    <>
      {/* ── Controls row ── */}
      <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search stock or sector..."
          style={{ fontSize:11, padding:'3px 8px', borderRadius:3,
            border:'.5px solid var(--hair-strong)', background:'var(--ibg)',
            color:'var(--ink)', outline:'none', width:160 }}/>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>
          {sorted.length} of {ranked.length} stocks
        </span>
        {search && (
          <button onClick={() => setSearch('')}
            style={{ fontSize:11, padding:'1px 7px', borderRadius:3, cursor:'pointer',
              border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', background:'transparent' }}>
            ✕ Clear
          </button>
        )}
        <div style={{ display:'flex', gap:3, marginLeft:'auto' }}>
          {['visual','table'].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              style={{ fontSize:11, padding:'2px 9px', borderRadius:10, cursor:'pointer',
                border:'.5px solid var(--hair-strong)',
                fontWeight: viewMode===m ? 700 : 400,
                background: viewMode===m ? 'var(--amber-ink)' : 'transparent',
                color: viewMode===m ? '#fff' : 'var(--ink-3)', transition:'all .15s' }}>
              {m === 'visual' ? 'Visual' : 'Table'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Visual view — table layout matching table-view column widths ── */}
      {viewMode === 'visual' && (
        <div className="tw" style={{ maxHeight:520, overflowY:'auto' }}>
          <table style={{ tableLayout:'fixed', width:'100%', minWidth:700, borderCollapse:'collapse' }}>
            <colgroup>
              <col style={{ width: SPARK_W }}/>
              <col style={{ width: NAME_W }}/>
              <col style={{ width: CAT_W }}/>
              <col/>  {/* RS Composite bar */}
              <col/>  {/* Price Composite bar */}
              <col style={{ width: 90 }}/>  {/* Score */}
              <col style={{ width: 18 }}/>  {/* Chevron */}
            </colgroup>
            <thead>
              <tr>
                <th style={{ padding:'5px 4px', position:'sticky', top:0, zIndex:2,
                  background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}/>
                <SH col="name">Stock</SH>
                <SH col="sector">Sector</SH>
                <th onClick={() => handleSort('totalRS')}
                  style={{ padding:'5px 8px', position:'sticky', top:0, zIndex:2, cursor:'pointer',
                    background:'var(--surface-2)', backgroundImage:'linear-gradient(rgba(59,130,246,.1),rgba(59,130,246,.1))',
                    borderBottom:'.5px solid var(--hair)', fontSize:11, fontWeight:700,
                    color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.04em',
                    whiteSpace:'nowrap', userSelect:'none', textAlign:'left' }}>
                  RS Composite
                  <span style={{ marginLeft:2, opacity: sort.col==='totalRS' ? 1 : .3, fontSize:11,
                    color: sort.col==='totalRS' ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
                    {sort.col==='totalRS' ? (sort.dir>0?'↑':'↓') : '↕'}
                  </span>
                  <span style={{ fontSize:10, fontWeight:400, opacity:.6, marginLeft:4 }}>/{maxRS}</span>
                </th>
                <th onClick={() => handleSort('totalP')}
                  style={{ padding:'5px 8px', position:'sticky', top:0, zIndex:2, cursor:'pointer',
                    background:'var(--surface-2)', backgroundImage:'linear-gradient(rgba(234,179,8,.08),rgba(234,179,8,.08))',
                    borderBottom:'.5px solid var(--hair)', fontSize:11, fontWeight:700,
                    color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'.04em',
                    whiteSpace:'nowrap', userSelect:'none', textAlign:'left' }}>
                  Price Composite
                  <span style={{ marginLeft:2, opacity: sort.col==='totalP' ? 1 : .3, fontSize:11,
                    color: sort.col==='totalP' ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
                    {sort.col==='totalP' ? (sort.dir>0?'↑':'↓') : '↕'}
                  </span>
                  <span style={{ fontSize:10, fontWeight:400, opacity:.6, marginLeft:4 }}>/{maxP}</span>
                </th>
                <SH col="totalRSP" right title="Total RS + Price score">
                  Score
                  <span style={{ fontSize:10, fontWeight:400, opacity:.6, marginLeft:2 }}>/{maxRSP}</span>
                </SH>
                <th style={{ padding:'5px 4px', position:'sticky', top:0, zIndex:2,
                  background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}/>
              </tr>
            </thead>
            <tbody>
              {sorted.map(row => {
                const rsBarPct  = maxRS  > 0 ? Math.round((row.totalRS  / maxRS)  * 100) : 0;
                const pBarPct   = maxP   > 0 ? Math.round((row.totalP   / maxP)   * 100) : 0;
                const totalPct  = maxRSP > 0 ? Math.round((row.totalRSP / maxRSP) * 100) : 0;
                const scoreColor = totalPct >= 80 ? 'var(--up-field-ink)'
                                 : totalPct <= 20 ? 'var(--down-field-ink)' : 'var(--ink)';
                const isOpen = !!expanded[row.name];
                const sparkColor = row.signal === 'Accelerating' ? 'var(--up-field-ink)'
                                 : row.signal === 'Decelerating' ? 'var(--down-field-ink)' : undefined;
                return (
                  <React.Fragment key={row.name}>
                    <tr onClick={() => toggleExpand(row.name)}
                      style={{ cursor:'pointer', borderBottom:'.5px solid var(--hair)' }}>
                      <td style={{ padding:'4px 4px' }}>
                        <Spark data={row.spark || []} w={SPARK_W - 8} h={18} color={sparkColor}/>
                      </td>
                      <td className="tlink thl" style={{ padding:'5px 6px', fontSize:11, fontWeight:600,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                        title={row.name}
                        onClick={e => { e.stopPropagation(); onOpen && onOpen({ name: row.name, sector: row.sector }); }}>
                        {row.name}
                      </td>
                      <td style={{ padding:'5px 6px', fontSize:11, color:'var(--ink-3)',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {row.sector}
                      </td>
                      {/* RS Composite bar */}
                      <td style={{ padding:'5px 8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:5, borderRadius:3, background:'var(--surface-2)', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${rsBarPct}%`,
                              background:'rgba(59,130,246,.65)', borderRadius:3 }}/>
                          </div>
                          <span style={{ fontSize:11, fontFamily:'var(--font-data)', color:'var(--ink-3)',
                            width:28, textAlign:'right', flexShrink:0 }}>{row.totalRS > 0 ? row.totalRS : '—'}</span>
                        </div>
                      </td>
                      {/* Price Composite bar */}
                      <td style={{ padding:'5px 8px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ flex:1, height:5, borderRadius:3, background:'var(--surface-2)', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pBarPct}%`,
                              background:'rgba(234,179,8,.65)', borderRadius:3 }}/>
                          </div>
                          <span style={{ fontSize:11, fontFamily:'var(--font-data)', color:'var(--ink-3)',
                            width:28, textAlign:'right', flexShrink:0 }}>{row.totalP > 0 ? row.totalP : '—'}</span>
                        </div>
                      </td>
                      {/* Total score */}
                      <td style={{ padding:'5px 6px', textAlign:'right', fontFamily:'var(--font-data)',
                        fontSize:13, fontWeight:800, color: scoreColor, whiteSpace:'nowrap' }}>
                        {row.totalRSP > 0 ? row.totalRSP : '—'}
                      </td>
                      {/* Chevron */}
                      <td style={{ padding:'5px 4px', textAlign:'center', fontSize:11, color:'var(--ink-3)' }}>
                        <span style={{ display:'inline-block', transition:'transform .2s',
                          transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
                      </td>
                    </tr>
                    {/* Expanded: 6 underlying ranks */}
                    {isOpen && (
                      <tr style={{ background:'var(--surface-2)' }}>
                        <td colSpan={7} style={{ padding:'8px 10px 10px',
                          borderBottom:'.5px solid var(--hair)' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:6 }}>
                            {[
                              { label:'RS 1W', val:row.rs1w, total:tRS1W, group:'RS' },
                              { label:'RS 1M', val:row.rs1m, total:tRS1M, group:'RS' },
                              { label:'RS 3M', val:row.rs3m, total:tRS3M, group:'RS' },
                              { label:'P 1W',  val:row.p1w,  total:tP1W,  group:'P'  },
                              { label:'P 1M',  val:row.p1m,  total:tP1M,  group:'P'  },
                              { label:'P 3M',  val:row.p3m,  total:tP3M,  group:'P'  },
                            ].map(({ label, val, total, group }) => {
                              const pct = val != null && total > 0 ? val / total : 0;
                              const c = pct >= 0.8 ? 'var(--up-field-ink)'
                                      : pct <= 0.2 ? 'var(--down-field-ink)' : 'var(--ink)';
                              const tint = group === 'RS' ? 'rgba(59,130,246,.5)' : 'rgba(234,179,8,.5)';
                              return (
                                <div key={label} style={{ textAlign:'center' }}>
                                  <div style={{ fontSize:10, color:'var(--ink-3)', marginBottom:3 }}>{label}</div>
                                  <div style={{ fontFamily:'var(--font-data)', fontSize:13, fontWeight:700,
                                    color: c }}>{val ?? '—'}</div>
                                  <div style={{ fontSize:10, color:'var(--ink-3)' }}>/{total}</div>
                                  <div style={{ height:3, borderRadius:2, background:'var(--hair)', marginTop:3, overflow:'hidden' }}>
                                    <div style={{ height:'100%', width:`${Math.round(pct*100)}%`,
                                      background: tint, borderRadius:2 }}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={7} style={{ padding:'24px', textAlign:'center',
                  color:'var(--ink-3)', fontSize:11 }}>No stocks match your filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Table view ── */}
      {viewMode === 'table' && (
      <>
      <div className="tw" style={{ maxHeight:520, overflowY:'auto' }}>
        <table style={{ tableLayout:'fixed', width:'100%', minWidth:1050, borderCollapse:'collapse' }}>
          <colgroup>
            <col style={{ width: SPARK_W }}/>
            <col style={{ width: NAME_W }}/>
            <col style={{ width: CAT_W }}/>
            {/* RS ranks: 1W, 1M, 3M, Total */}
            <col/><col/><col/><col/>
            {/* Price ranks: 1W, 1M, 3M, Total */}
            <col/><col/><col/><col/>
            {/* Total RS & P Score */}
            <col/>
          </colgroup>
          <thead>
            {/* Single-row header — RS columns tinted blue, Price columns tinted amber, Combined purple */}
            <tr>
              <th style={{ padding:'5px 4px', position:'sticky', top:0, zIndex:2,
                background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}/>
              <SH col="name">Stock</SH>
              <SH col="sector">Sector</SH>
              <SH col="rs1w"    right bg="rgba(59,130,246,.07)"  title="RS Rank 1W — RS-change rank over 5 sessions">RS 1W</SH>
              <SH col="rs1m"    right bg="rgba(59,130,246,.07)"  title="RS Rank 1M — RS-change rank over 21 sessions">RS 1M</SH>
              <SH col="rs3m"    right bg="rgba(59,130,246,.07)"  title="RS Rank 3M — RS-change rank over 63 sessions">RS 3M</SH>
              <SH col="totalRS" right bg="rgba(59,130,246,.14)"  title="Total RS Score = RS1W + RS1M + RS3M ranks">RS Total</SH>
              <SH col="p1w"     right bg="rgba(234,179,8,.07)"   title="Price Rank 1W — price % change rank over 5 sessions">P 1W</SH>
              <SH col="p1m"     right bg="rgba(234,179,8,.07)"   title="Price Rank 1M — price % change rank over 21 sessions">P 1M</SH>
              <SH col="p3m"     right bg="rgba(234,179,8,.07)"   title="Price Rank 3M — price % change rank over 63 sessions">P 3M</SH>
              <SH col="totalP"  right bg="rgba(234,179,8,.14)"   title="Total Price Score = P1W + P1M + P3M ranks">P Total</SH>
              <SH col="totalRSP" right bg="rgba(168,85,247,.1)"  title="Total RS & Price Score — default sort">RS+P</SH>
            </tr>
          </thead>
          <tbody>
            {sorted.map(row => {
              const sparkColor = row.signal === 'Accelerating' ? 'var(--up-field-ink)'
                               : row.signal === 'Decelerating' ? 'var(--down-field-ink)' : undefined;
              return (
                <tr key={row.name}>
                  <td style={{ padding:'4px 4px' }}>
                    <Spark data={row.spark || []} w={SPARK_W - 8} h={20} color={sparkColor}/>
                  </td>
                  <td className="tlink thl" style={{ padding:'5px 6px', overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:11 }}
                    title={row.name}
                    onClick={() => onOpen && onOpen({ name: row.name, sector: row.sector })}>
                    {row.name}
                  </td>
                  <td style={{ padding:'5px 6px', fontSize:11, color:'var(--ink-3)',
                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {row.sector}
                  </td>
                  {/* RS ranks */}
                  <RankCell r={row.rs1w} t={tRS1W}/>
                  <RankCell r={row.rs1m} t={tRS1M}/>
                  <RankCell r={row.rs3m} t={tRS3M}/>
                  <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:11, fontWeight:700,
                    color: scoreCol(row.totalRS, maxRS) }}>
                    {row.totalRS > 0 ? row.totalRS : '—'}
                  </td>
                  {/* Price ranks */}
                  <RankCell r={row.p1w} t={tP1W}/>
                  <RankCell r={row.p1m} t={tP1M}/>
                  <RankCell r={row.p3m} t={tP3M}/>
                  <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:11, fontWeight:700,
                    color: scoreCol(row.totalP, maxP) }}>
                    {row.totalP > 0 ? row.totalP : '—'}
                  </td>
                  {/* RS+P combined */}
                  <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:12, fontWeight:700,
                    color: scoreCol(row.totalRSP, maxRSP) }}>
                    {row.totalRSP > 0 ? row.totalRSP : '—'}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={13} style={{ padding:'24px', textAlign:'center',
                color:'var(--ink-3)', fontSize:11 }}>No stocks match your filter</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:6 }}>
        Rank = position among all stocks (higher = better) · RS Rank = RS ratio change · Price Rank = raw price % change ·
        Top 20% <span style={{ color:'var(--up-field-ink)' }}>green</span> · Bottom 20% <span style={{ color:'var(--down-field-ink)' }}>red</span>
      </div>
      </>
      )}
    </>
  );
}

// ── Section 6 — Top-Down RS: Sector → Stock ───────────────────────────────────

const SIG_COL = {
  Accelerating:   { dot: 'var(--up-field-ink)',   bg: 'rgba(26,106,26,.12)',   text: 'var(--up-field-ink)'  },
  Decelerating: { dot: 'var(--down-field-ink)',   bg: 'rgba(196,26,26,.12)',   text: 'var(--down-field-ink)'  },
  Flat:         { dot: 'var(--amber-ink)', bg: 'rgba(200,146,42,.12)', text: 'var(--amber-ink)' },
};

// Shared header style for both outer and inner table
const TD_TH = {
  padding: '5px 8px', cursor: 'pointer', userSelect: 'none',
  color: 'var(--ink-3)', fontSize: 11, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.04em',
  whiteSpace: 'nowrap', background: 'var(--surface-2)',
  borderBottom: '.5px solid var(--hair)',
};

// Staleness threshold matching backend _RS_STALE_HOURS (28 hours).
// Yesterday's data intraday is normal; alarm only when a full session was missed.
const _TOPDOWN_STALE_MS = 28 * 60 * 60 * 1000;

function TopdownSection({ onOpen }) {
  const [data,            setData]            = useState(null);
  const [open,            setOpen]            = useState(_RS_SECTIONS.topdownOpen);
  const [expanded,        setExpanded]        = useState({});
  const [secSort,         setSecSort]         = useState({ col: 'sec_rs_1m', dir: -1 });
  const [stSort,          setStSort]          = useState({ col: 'rs_1m_sector', dir: -1 });
  const [error,           setError]           = useState(null);
  const [computedAt,      setComputedAt]      = useState(null);   // topdown_computed_at ISO string
  const [sectorsExpected, setSectorsExpected] = useState(null);
  const [sectorsReturned, setSectorsReturned] = useState(null);
  const [sectorsMissing,  setSectorsMissing]  = useState([]);

  useEffect(() => { _RS_SECTIONS.topdownOpen = open; }, [open]);

  useEffect(() => {
    let cancelled = false;
    getTopdownRS()
      .then(res => {
        if (cancelled) return;
        if (res._error) { setError('Top-down RS unavailable'); setData([]); return; }
        setData(res.sectors || []);
        setComputedAt(res.topdown_computed_at || null);
        setSectorsExpected(res.sectors_expected ?? null);
        setSectorsReturned(res.sectors_returned ?? null);
        setSectorsMissing(res.sectors_missing  || []);
      })
      .catch(() => { if (!cancelled) { setError('Network error'); setData([]); } });
    return () => { cancelled = true; };
  }, []);

  // Derived freshness state
  const isStale = computedAt
    ? (Date.now() - new Date(computedAt).getTime()) > _TOPDOWN_STALE_MS
    : false;
  const isPartial = sectorsExpected !== null && sectorsReturned !== null
    && sectorsReturned < sectorsExpected;

  const asOfLabel = computedAt
    ? new Date(computedAt).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata',
      })
    : null;

  const toggle = sector => setExpanded(e => ({ ...e, [sector]: !e[sector] }));
  const collapseAll = () => setExpanded({});
  const expandAll   = () => {
    if (!data) return;
    const all = {};
    data.forEach(s => { all[s.sector] = true; });
    setExpanded(all);
  };

  // Composite rank: Strong.Up=0 > Strong.Down=1 > Equal.*=2 > Weak.Up=3 > Weak.Down=4
  const _compositeRank = sec => {
    const strength = _rsStrength((sec.sec_rs_vs_nifty ?? 0) / 100);
    const momentum = sec.sec_signal || 'Flat';
    if (strength === 'Strong' && momentum === 'Accelerating') return 0;
    if (strength === 'Strong') return 1;
    if (strength === 'Equal') return 2;
    if (strength === 'Weak' && momentum === 'Accelerating') return 3;
    return 4;
  };

  // Sector sort
  const sortedSectors = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      if (secSort.col === 'sector') return a.sector.localeCompare(b.sector) * secSort.dir;
      if (secSort.col === 'outperforming_vs_nifty')
        return ((a.outperforming_vs_nifty ?? 0) - (b.outperforming_vs_nifty ?? 0)) * secSort.dir;
      if (secSort.col === 'sec_signal') {
        // dir:-1 = strongest first (rank 0 first), dir:1 = weakest first
        return (_compositeRank(a) - _compositeRank(b)) * secSort.dir;
      }
      return ((a[secSort.col] ?? -999) - (b[secSort.col] ?? -999)) * secSort.dir;
    });
  }, [data, secSort]);

  // Stock sort — applies to all expanded sectors
  const sortStocks = stocks => [...stocks].sort((a, b) => {
    if (stSort.col === 'symbol') return a.symbol.localeCompare(b.symbol) * stSort.dir;
    if (stSort.col === 'signal_vs_sector')
      return (a.signal_vs_sector || '').localeCompare(b.signal_vs_sector || '') * stSort.dir;
    if (stSort.col === 'signal_vs_nifty')
      return (a.signal_vs_nifty || '').localeCompare(b.signal_vs_nifty || '') * stSort.dir;
    return ((a[stSort.col] ?? -999) - (b[stSort.col] ?? -999)) * stSort.dir;
  });

  const makeSH = (setSort, sortState) => ({ col, children, right, bg, paddingLeft }) => {
    const active = sortState.col === col;
    return (
      <th onClick={() => setSort(s => s.col === col ? { ...s, dir: -s.dir } : { col, dir: -1 })}
        style={{ ...TD_TH, textAlign: right ? 'right' : 'left',
          position: 'sticky', top: 0, zIndex: 2,
          ...(bg && { background: bg }),
          ...(paddingLeft && { paddingLeft }),
          borderBottom: '.5px solid var(--hair)',
          cursor: 'pointer' }}>
        {children}
        <span style={{ marginLeft: 2, opacity: active ? 1 : .3, fontSize: 11,
          color: active ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
          {active ? (sortState.dir > 0 ? '↑' : '↓') : '↕'}
        </span>
      </th>
    );
  };
  const SecSH = makeSH(setSecSort, secSort);
  const StSH  = makeSH(setStSort,  stSort);

  const fmtRS  = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
  const rsColor = v => v == null ? 'var(--ink-3)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';

  const OUTER_COL1 = 180;
  const INNER_COL1 = 180;

  // Signal → left-border color for sector rows
  const SIG_BORDER = {
    Accelerating:   'var(--up-field-ink)',
    Decelerating: 'var(--down-field-ink)',
    Flat:         'var(--amber-ink)',
  };

  // SigPill uses CompoundPill — strength from rs_ratio if available, else Equal
  const SigPill = ({ signal, rsRatio }) => (
    <CompoundPill strength={_rsStrength(rsRatio)} momentum={signal || 'Flat'}/>
  );

  // Collapse / Expand All button strip
  const ctrlBtns = open && (
    <div style={{ display:'flex', gap:4 }}>
      <button onClick={collapseAll}
        style={{ fontSize:11, padding:'2px 8px', borderRadius:3, cursor:'pointer',
          border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', background:'transparent' }}>
        Collapse All
      </button>
      <button onClick={expandAll}
        style={{ fontSize:11, padding:'2px 8px', borderRadius:3, cursor:'pointer',
          border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', background:'transparent' }}>
        Expand All
      </button>
    </div>
  );

  return (
    <div>
      {/* ── Section header — matches SecHdr: arrow next to title, fontSize:11 label ── */}
      <div style={{ margin:'14px 0 7px', display:'flex', alignItems:'center',
        justifyContent:'space-between', flexWrap:'wrap', gap:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
        <button onClick={() => setOpen(o => !o)}
          style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none',
            cursor:'pointer', padding:0, color:'var(--ink-3)' }}>
          <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase' }}>
            Top Down Approach — Sector {'>'} Stocks — F&amp;O Stocks
            {data && data.length > 0 && (
              <span style={{ fontWeight:500, opacity:.6, marginLeft:5 }}>
                ({data.length} sectors · {data.reduce((s, sec) => s + (sec.stock_count || 0), 0)} stocks)
              </span>
            )}
          </span>
          <span style={{ fontSize:16, lineHeight:1, transition:'transform .2s',
            display:'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            color:'var(--ink-2)' }}>▾</span>
        </button>
        <InfoBtn>Top-Down workflow: pick a strong sector, drill into its stocks. Sector pill = vs NIFTY. Stock pills: vs Sector and vs NIFTY.</InfoBtn>
        </div>{/* end left flex group */}
        {ctrlBtns}
      </div>

      {open && (
        <>
          {data === null && (
            <div style={{ padding:'20px 0', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
              Loading top-down RS…
            </div>
          )}
          {error && (
            <div style={{ fontSize:11, color:'var(--amber-ink)', padding:'6px 0' }}>{error}</div>
          )}
          {data && data.length === 0 && !error && (
            <div style={{ padding:'16px 0', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
              No top-down data yet — trigger a Sync or wait for the next scheduled refresh.
            </div>
          )}

          {/* ── Freshness + completeness status bar ── */}
          {!error && data !== null && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:6, alignItems:'center' }}>
              {/* Always-on quiet as-of label */}
              {asOfLabel && (
                <span style={{ fontSize:10, color: isStale ? 'var(--amber-ink)' : 'var(--ink-3)', opacity: isStale ? 1 : 0.7 }}>
                  {isStale
                    ? <><span style={{ fontWeight:700 }}>⚠</span> stale — last updated {asOfLabel}</>
                    : <>as of {asOfLabel}</>
                  }
                </span>
              )}
              {/* Partial-result indicator — only when sectors are missing */}
              {isPartial && (
                <span style={{
                  fontSize:10, fontWeight:600, color:'var(--amber-ink)',
                  padding:'1px 6px', borderRadius:3,
                  background:'rgba(200,146,42,.12)', border:'.5px solid rgba(200,146,42,.35)',
                }}>
                  ⚠ incomplete — showing {sectorsReturned} of {sectorsExpected} sectors
                  {sectorsMissing.length > 0 && ` (missing: ${sectorsMissing.join(', ')})`}
                </span>
              )}
            </div>
          )}

          {sortedSectors.length > 0 && (
            <div className="tw" style={{ maxHeight: 700, overflowY:'auto' }}>
              <table style={{ tableLayout:'fixed', width:'100%', minWidth:700, borderCollapse:'collapse' }}>
                <colgroup>
                  <col style={{ width: OUTER_COL1 }}/>
                  <col/><col/><col/><col/>      {/* RS 1W / 1M / 3M / 20W */}
                  <col style={{ width: 148 }}/>  {/* Signal pill */}
                  <col style={{ width: 36 }}/>   {/* WoW */}
                  <col style={{ width: 110 }}/>  {/* X/Y outperforming */}
                  <col style={{ width: 28 }}/>   {/* expand toggle */}
                </colgroup>
                <thead>
                  <tr>
                    <SecSH col="sector">Sector</SecSH>
                    <SecSH col="sec_rs_1w" right>vs Nifty 1W</SecSH>
                    <SecSH col="sec_rs_1m" right>vs Nifty 1M</SecSH>
                    <SecSH col="sec_rs_3m" right>vs Nifty 3M</SecSH>
                    <SecSH col="sec_rs_vs_nifty" right>vs Nifty 20W</SecSH>
                    <SecSH col="sec_signal">Signal vs NIFTY</SecSH>
                    <th style={{ ...TD_TH, position:'sticky', top:0, zIndex:2, textAlign:'center', fontSize:10, color:'var(--ink-3)' }}>WoW</th>
                    <SecSH col="outperforming_vs_nifty" right>Outperforming</SecSH>
                    <th style={{ ...TD_TH, position:'sticky', top:0, zIndex:2 }}/>
                  </tr>
                </thead>
                <tbody>
                  {sortedSectors.map(sec => {
                    const isOpen      = !!expanded[sec.sector];
                    const rs20w = (sec.sec_rs_vs_nifty ?? 100) / 100;
                    const borderColor = rs20w > 1.005 ? 'var(--up-field-ink)' : rs20w < 0.995 ? 'var(--down-field-ink)' : 'var(--amber-ink)';
                    // Subtle tinted bg based on signal
                    const secBg = sec.sec_signal === 'Accelerating'
                      ? 'rgba(26,106,26,.06)'
                      : sec.sec_signal === 'Decelerating'
                      ? 'rgba(196,26,26,.05)'
                      : 'rgba(200,146,42,.04)';

                    return (
                      <React.Fragment key={sec.sector}>
                        {/* ── Sector row — darkens when expanded to merge visually with stock block ── */}
                        <tr onClick={() => toggle(sec.sector)}
                          style={{ cursor:'pointer',
                            background: isOpen ? 'var(--surface-2)' : 'var(--surface)',
                            borderLeft: `3px solid ${borderColor}`,
                            borderTop: '1px solid var(--hair-strong)' }}>
                          <td style={{ padding:'7px 8px 7px 10px', fontWeight:700, fontSize:12,
                            color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis',
                            whiteSpace:'nowrap', textAlign:'left' }}>
                            {sec.sector}
                            {sec.is_synthetic && (
                              <span style={{ fontSize:11, fontWeight:700, color:'#a78bfa',
                                marginLeft:5, letterSpacing:'.03em', opacity:.9 }}>(S)</span>
                            )}
                            <span style={{ fontSize:10, color:'var(--ink-3)', fontWeight:400, marginLeft:5 }}>
                              ({sec.stock_count ?? 0})
                            </span>
                          </td>
                          {[sec.sec_rs_1w, sec.sec_rs_1m, sec.sec_rs_3m].map((v, i) => (
                            <td key={i} style={{ ...NUM_STYLE, textAlign:'right', fontSize:11,
                              fontFamily:'var(--font-data)', fontWeight:600, color:rsColor(v) }}>
                              {fmtRS(v)}
                            </td>
                          ))}
                          <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:11,
                            fontFamily:'var(--font-data)', fontWeight:600,
                            color: rsColor(sec.sec_rs_vs_nifty != null ? sec.sec_rs_vs_nifty - 100 : null) }}>
                            {sec.sec_rs_vs_nifty != null ? fmtRS(sec.sec_rs_vs_nifty - 100) : '—'}
                          </td>
                          <td style={{ padding:'5px 8px' }}>
                            <SigPill signal={sec.sec_signal} rsRatio={sec.sec_rs_vs_nifty / 100}/>
                          </td>
                          <td style={{ textAlign:'center', fontSize:13, color:
                            sec.wow_strength_change === 'up' ? 'var(--up-field-ink)' :
                            sec.wow_strength_change === 'down' ? 'var(--down-field-ink)' : 'var(--ink-3)'
                          }}>
                            {sec.wow_strength_change === 'up' ? '↑' : sec.wow_strength_change === 'down' ? '↓' : '—'}
                          </td>
                          <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:11, color:'var(--ink-3)' }}>
                            <span style={{ color:'var(--up-field-ink)', fontWeight:700 }}>
                              {sec.outperforming_vs_nifty ?? sec.outperforming ?? 0}
                            </span>
                            /{sec.stock_count}
                          </td>
                          <td style={{ padding:'5px 8px', textAlign:'center',
                            fontSize:11, color:'var(--ink-3)' }}>
                            <span style={{ display:'inline-block', transition:'transform .15s',
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
                          </td>
                        </tr>

                        {/* ── Expanded stock sub-table ── */}
                        {isOpen && (
                          <tr>
                            <td colSpan={9} style={{ padding:'0 0 6px 0',
                              background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)',
                              borderLeft:`3px solid ${borderColor}`,
                              transition:'max-height .18s ease' }}>
                              <table style={{ tableLayout:'fixed', width:'100%',
                                borderCollapse:'collapse', minWidth:600 }}>
                                <colgroup>
                                  <col style={{ width: OUTER_COL1 }}/>  {/* matches Sector col */}
                                  <col/><col/><col/><col/>               {/* 1W / 1M / 3M / 20W */}
                                  <col style={{ width: 148 }}/>          {/* VS SECTOR pill — matches Signal col */}
                                  <col style={{ width: 36 }}/>           {/* blank — matches WoW col */}
                                  <col style={{ width: 110 }}/>          {/* VS NIFTY — matches Outperforming col */}
                                  <col style={{ width: 28 }}/>           {/* blank — matches toggle col */}
                                </colgroup>
                                <thead>
                                  <tr>
                                    <StSH col="symbol" bg="var(--surface-2)" paddingLeft={28}>STOCK</StSH>
                                    <StSH col="rs_1w_sector"     right bg="var(--surface-2)">VS SEC 1W</StSH>
                                    <StSH col="rs_1m_sector"     right bg="var(--surface-2)">VS SEC 1M</StSH>
                                    <StSH col="rs_3m_sector"     right bg="var(--surface-2)">VS SEC 3M</StSH>
                                    <StSH col="rs_vs_sector"     right bg="var(--surface-2)">VS SEC 20W</StSH>
                                    <StSH col="signal_vs_sector" right bg="var(--surface-2)">VS SECTOR</StSH>
                                    <th style={{ ...TD_TH, position:'sticky', top:0, zIndex:1,
                                      background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}/>
                                    <StSH col="signal_vs_nifty"  right bg="var(--surface-2)">VS NIFTY</StSH>
                                    <th style={{ ...TD_TH, position:'sticky', top:0, zIndex:1,
                                      background:'var(--surface-2)', borderBottom:'.5px solid var(--hair)' }}/>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortStocks(sec.stocks).map(st => (
                                    <tr key={st.symbol}
                                      style={{ background:'var(--surface-2)' }}>
                                      {/* Stock name — indented 2× relative to sector name */}
                                      <td className="tlink thl"
                                        style={{ padding:'4px 8px 4px 28px', fontSize:11,
                                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                          textAlign:'left' }}
                                        onClick={() => onOpen && onOpen({ name: st.symbol, sector: sec.sector })}>
                                        <span style={{ color:'var(--hair-strong)', marginRight:6, fontSize:11,
                                          fontFamily:'monospace' }}>└─</span>
                                        {st.symbol}
                                      </td>
                                      {[st.rs_1w_sector, st.rs_1m_sector, st.rs_3m_sector].map((v, i) => (
                                        <td key={i} style={{ ...NUM_STYLE, textAlign:'right', fontSize:11,
                                          fontFamily:'var(--font-data)', color:rsColor(v) }}>
                                          {fmtRS(v)}
                                        </td>
                                      ))}
                                      {/* VS SEC 20W — rs_vs_sector centred at 100 */}
                                      <td style={{ ...NUM_STYLE, textAlign:'right', fontSize:11,
                                        fontFamily:'var(--font-data)',
                                        color: rsColor(st.rs_vs_sector != null ? st.rs_vs_sector - 100 : null) }}>
                                        {st.rs_vs_sector != null ? fmtRS(st.rs_vs_sector - 100) : '—'}
                                      </td>
                                      {/* vs Sector pill — col 6, aligns with Signal vs NIFTY (148px) */}
                                      <td style={{ padding:'4px 8px', textAlign:'right', verticalAlign:'middle' }}>
                                        <SigPill signal={st.signal_vs_sector} rsRatio={st.rs_vs_sector / 100}/>
                                      </td>
                                      {/* blank — col 7, aligns with WoW (36px) */}
                                      <td/>
                                      {/* VS NIFTY pill — col 8, aligns with Outperforming (110px) */}
                                      <td style={{ padding:'4px 8px', textAlign:'right', verticalAlign:'middle' }}>
                                        {st.signal_vs_nifty
                                          ? <SigPill signal={st.signal_vs_nifty} rsRatio={st.rs_vs_nifty}/>
                                          : <span style={{ fontSize:11, color:'var(--ink-3)' }}>—</span>}
                                      </td>
                                      <td/>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize:11, color:'var(--ink-3)', marginTop:6 }}>
            RS 1W/1M/3M = change in RS ratio over 5/21/63 sessions ·
            RS 20W = RS ratio vs base 100 trading days ago (same horizon as the CompoundPill) ·
            Sector signal vs NIFTY · Stock signal vs its sector index ·
            Outperforming = stocks beating NIFTY 50 · Sectors sorted by RS 1M by default ·{' '}
            <span style={{ color:'#a78bfa', fontWeight:600 }}>(S)</span> = synthetic index
            (custom basket, not a NSE official index)
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Tab ──────────────────────────────────────────────────────────────────

// Plain section label (non-collapsible)
const SL = ({ children }) => (
  <div style={{fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
    color:'var(--ink-3)', margin:'14px 0 7px'}}>{children}</div>
);

// Unified collapsible section header — consistent across all collapsible sections.
// Arrow immediately follows title, same fontSize:11 label, fontSize:16 triangle.
// infoText: if provided, adds an (i) button with a popover.
function SecHdr({ open, onToggle, children, extra, infoText }) {
  return (
    <div style={{ margin:'14px 0 7px', display:'flex', alignItems:'center',
      justifyContent:'space-between', flexWrap:'wrap', gap:6 }}>
      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
        <button onClick={onToggle}
          style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none',
            cursor:'pointer', padding:0, color:'var(--ink-3)' }}>
          <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase' }}>
            {children}
          </span>
          <span style={{ fontSize:16, lineHeight:1, transition:'transform .2s',
            display:'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            color:'var(--ink-2)' }}>▾</span>
        </button>
        {infoText && <InfoBtn>{infoText}</InfoBtn>}
      </div>
      {extra}
    </div>
  );
}

// Two-level chart sort order: signal group first, then ratio descending within group.
// Proxy / no-data entries always go to the bottom regardless of signal.
const SIG_ORDER = { 'Accelerating': 0, 'Flat': 1, 'Decelerating': 2 };

// ── RS signal with ±0.5% neutral band ────────────────────────────────────────
// RS ratio within 0.5% of its 20-day MA → Flat (not convincingly directional).
// This also ensures NIFTY 50 (RS vs itself ≡ 1.000) never reads as Decelerating.
const RS_NEUTRAL_BAND = 0.005;
function _rsSignal(last, maLast) {
  if (last == null || maLast == null || maLast === 0) return 'Flat';
  const dev = (last - maLast) / maLast;
  if (dev >  RS_NEUTRAL_BAND) return 'Accelerating';
  if (dev < -RS_NEUTRAL_BAND) return 'Decelerating';
  return 'Flat';
}

// ── RS strength tier from ratio ───────────────────────────────────────────────
function _rsStrength(rsRatio) {
  if (rsRatio == null) return 'Equal';
  if (rsRatio > 1.005) return 'Strong';
  if (rsRatio < 0.995) return 'Weak';
  return 'Equal';
}

// Category filter pills — "All" first, then logical groups.
// "Sectors" groups Index + Sector + Thematic (equity market indices).
// "Broad Market" covers Broad category (Nifty 100/200/500/Midcap etc.).
// "Crypto & FX" merges Crypto + Currency into one pill.
const CHART_FILTERS = ['All', 'Sectors', 'Broad Market', 'Commodities', 'Crypto & FX', 'Debt'];
const _SECTOR_CATS  = new Set(['Index', 'Sector', 'Thematic']);
function matchFilter(cat, f) {
  if (f === 'All')          return true;
  if (f === 'Sectors')      return _SECTOR_CATS.has(cat);
  if (f === 'Broad Market') return cat === 'Broad';
  if (f === 'Commodities')  return cat === 'Commodity';
  if (f === 'Crypto & FX')  return cat === 'Crypto' || cat === 'Currency';
  if (f === 'Debt')         return cat === 'Debt';
  return false;
}

export default function RelativeStrengthTab() {
  const { open: openPanel } = useSidePanel();

  // ── API data state — initialised from module cache if available ───────────
  const [allData,      setAllData]      = useState(_RS_CACHE?.allData      || []);
  const [allStockData, setAllStockData] = useState(_RS_CACHE?.allStockData || []);
  const [loading,      setLoading]      = useState(!_RS_CACHE);
  const [assetError,   setAssetError]   = useState(false);  // asset RS failed, stock RS ok
  const [error,        setError]        = useState(null);   // both failed
  const [stale,        setStale]        = useState(_RS_CACHE?.stale        || false);
  const [computedAt,   setComputedAt]   = useState(_RS_CACHE?.computedAt   || null);

  // ── Nifty 50 price history for the focus chart ─────────────────────────────
  const [niftyHistory, setNiftyHistory] = useState([]);

  const doFetch = useCallback((force = false) => {
    if (_RS_CACHE && !force) return; // skip if cached and not forced
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAssetError(false);

    Promise.all([getAssetRS(), getFoStockRS()])
      .then(([assetRes, stockRes]) => {
        if (cancelled) return;
        if (assetRes._error && stockRes._error) {
          setError('Could not load RS data. Backend may still be computing.');
          return;
        }
        const newAllData      = (assetRes.assets || []).map(transformAsset);
        const newAllStockData = (stockRes.stocks || []).map(transformStock);
        const computedAt      = assetRes.computed_at || stockRes.computed_at || null;
        const stale           = !!(assetRes._stale || stockRes._stale);

        // Only write to cache when we have complete data — prevents a failed
        // asset fetch from permanently locking in an empty allData.
        if (newAllData.length > 0) {
          _RS_CACHE = { allData: newAllData, allStockData: newAllStockData, computedAt, stale };
        }
        if (assetRes._error || newAllData.length === 0) {
          setAssetError(true);  // show retry banner but still display stock table
        }
        setAllData(newAllData);
        setAllStockData(newAllStockData);
        setComputedAt(computedAt);
        setStale(stale);
      })
      .catch(() => {
        if (!cancelled) setError('Network error loading RS data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => doFetch(), [doFetch]);

  // Fetch Nifty 50 price history for the focus chart (1 year)
  useEffect(() => {
    getNiftyHistory(252)
      .then(res => { if (res.rows) setNiftyHistory(res.rows); })
      .catch(() => {});
  }, []);

  // ── Chart filter + sort state ─────────────────────────────────────────────
  const [chartFilter,    setChartFilter]    = useState('All');
  const [barChartOpen,   setBarChartOpen]   = useState(_RS_SECTIONS.barChartOpen);
  const [chartsOpen,     setChartsOpen]     = useState(_RS_SECTIONS.chartsOpen);
  const [legendOpen,     setLegendOpen]     = useState(_RS_SECTIONS.legendOpen);
  const [crosshairDate,  setCrosshairDate]  = useState(null);
  const [methWide,       setMethWide]       = useState(() => window.innerWidth > 720);
  useEffect(() => {
    const h = () => setMethWide(window.innerWidth > 720);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Collapsible section state — sections 3-7 all start collapsed ──────────
  const [rankingsOpen,  setRankingsOpen]  = useState(_RS_SECTIONS.rankingsOpen);
  const [rsSummaryOpen, setRsSummaryOpen] = useState(_RS_SECTIONS.rsSummaryOpen);
  const [mtfScope,      setMtfScope]      = useState('assets'); // Section 6 scope toggle

  // Persist section open/closed state to the module-level object so that
  // switching sub-tabs and coming back keeps all sections in their last state.
  useEffect(() => {
    _RS_SECTIONS.barChartOpen  = barChartOpen;
    _RS_SECTIONS.chartsOpen    = chartsOpen;
    _RS_SECTIONS.legendOpen    = legendOpen;
    _RS_SECTIONS.rankingsOpen  = rankingsOpen;
    _RS_SECTIONS.rsSummaryOpen = rsSummaryOpen;
  }, [barChartOpen, chartsOpen, legendOpen, rankingsOpen, rsSummaryOpen]);

  // Look up the three focus RS assets from allData
  const gsecEntry      = useMemo(() => allData.find(d => d.asset.id === 'gsec10'),   [allData]);
  const usdinrEntry    = useMemo(() => allData.find(d => d.asset.id === 'usdinr'),   [allData]);
  const goldEntry      = useMemo(() => allData.find(d => d.asset.id === 'gold'),     [allData]);
  const sp500Entry     = useMemo(() => allData.find(d => d.asset.id === 'sp500'),    [allData]);
  const hangsengEntry  = useMemo(() => allData.find(d => d.asset.id === 'hangseng'), [allData]);

  // ── Focus chart ranking — rank 6 assets by RS ratio (highest = #1) ────────
  // NIFTY 50 is the benchmark, its RS vs itself ≡ 1.000 (always).
  // Rank only the 4 core capital-flow assets (NIFTY, G-Sec, USD/INR, Gold).
  // S&P 500 + Hang Seng are context tiles — no rank circle.
  const focusRanks = useMemo(() => {
    const candidates = [
      { id: 'nifty',  rs: 1.0 },
      { id: 'gsec10', rs: gsecEntry?.stats?.last   ?? 0 },
      { id: 'usdinr', rs: usdinrEntry?.stats?.last ?? 0 },
      { id: 'gold',   rs: goldEntry?.stats?.last   ?? 0 },
    ].sort((a, b) => b.rs - a.rs);
    const out = {};
    candidates.forEach((c, i) => { out[c.id] = i + 1; });
    return out;
  }, [gsecEntry, usdinrEntry, goldEntry]);

  // Charts: filter by category, then sort by:
  //   1. Signal group  — Accelerating → Flat → Decelerating
  //   2. 20W ratio desc within each group (highest RS first)
  //   Proxy / no-data entries always rendered last.
  const chartData = useMemo(() => {
    const filtered = allData.filter(d => matchFilter(d.asset.cat, chartFilter));
    return [...filtered].sort((a, b) => {
      // Proxy / error / no-data entries always last
      const aProxy = a.isProxy || a.isError || a.stats?.last == null;
      const bProxy = b.isProxy || b.isError || b.stats?.last == null;
      if (aProxy && !bProxy) return 1;
      if (!aProxy && bProxy) return -1;
      if (aProxy && bProxy)  return 0;

      // Primary: signal group order
      const as = SIG_ORDER[a.stats?.signal] ?? 1;
      const bs = SIG_ORDER[b.stats?.signal] ?? 1;
      if (as !== bs) return as - bs;

      // Secondary: ratio descending within group
      const av = a.stats?.last ?? 0;
      const bv = b.stats?.last ?? 0;
      return bv - av;
    });
  }, [allData, chartFilter]);

  // ── Filter/sort state ──────────────────────────────────────────────────────
  const [stockSearch,  setStockSearch] = useState('');
  const [stockSector,  setStockSector] = useState('All');

  // ── Section 1 (i) info popover ────────────────────────────────────────────

  const handleOpen = useCallback((asset) => {
    const isAssetClass = !!(asset.cat);
    openPanel({
      name:   asset.name || asset,
      sector: isAssetClass ? 'Index' : (asset.sector || ''),
    });
  }, [openPanel]);

  const stockSectors = useMemo(() =>
    ['All', ...Array.from(new Set(allStockData.map(r => r.sector))).sort()],
    [allStockData]
  );

  const filteredStocks = useMemo(() => {
    let rows = allStockData;
    if (stockSector !== 'All') rows = rows.filter(r => r.sector === stockSector);
    if (stockSearch.trim()) rows = rows.filter(r =>
      r.name.toLowerCase().includes(stockSearch.toLowerCase()) ||
      r.sector.toLowerCase().includes(stockSearch.toLowerCase())
    );
    return rows;
  }, [allStockData, stockSector, stockSearch]);

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{padding:'48px 0', textAlign:'center', color:'var(--ink-3)', fontSize:11}}>
        Loading RS data… (first load may take 30–60 s while backend computes)
      </div>
    );
  }

  if (error) {
    return (
      <div style={{padding:'48px 0', textAlign:'center', color:'var(--down-field-ink)', fontSize:11}}>
        {error}
      </div>
    );
  }

  const fmtComputedAt = computedAt
    ? new Date(computedAt).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', timeZone:'Asia/Kolkata' }) + ' IST'
    : null;

  // Helper to render a focus RS chart (or placeholder while data loads)
  const focusRSChart = (entry, fallbackLabel, rank) => {
    if (!entry) {
      return (
        <div className="card" style={{padding:'8px 10px', display:'flex', alignItems:'center',
          justifyContent:'center', minHeight:218, color:'var(--ink-3)', fontSize:11}}>
          {fallbackLabel} · loading…
        </div>
      );
    }
    return (
      <RSChart
        series={entry.series}
        label={entry.asset.name}
        stats={entry.stats}
        onOpen={handleOpen}
        asset={entry.asset}
        isProxy={entry.isProxy}
        isError={entry.isError}
        chartHeight={160}
        crosshairDate={crosshairDate}
        onCrosshairChange={setCrosshairDate}
        rank={rank}
      />
    );
  };

  return (
    <div>
      {/* ── Asset RS error/retry banner ── */}
      {assetError && (
        <div style={{display:'flex', alignItems:'center', gap:10, padding:'7px 12px',
          borderRadius:4, background:'rgba(200,146,42,.1)', border:'.5px solid var(--amber-ink)',
          marginBottom:10, fontSize:11, color:'var(--amber-ink)'}}>
          <span>⚠ Asset RS data unavailable (backend may still be computing).</span>
          <button onClick={() => { _RS_CACHE = null; doFetch(true); }}
            style={{fontSize:11, padding:'2px 8px', borderRadius:3, cursor:'pointer',
              border:'.5px solid var(--amber-ink)', color:'var(--amber-ink)', background:'transparent',
              fontWeight:700, marginLeft:'auto'}}>
            <IconRefresh size={12} style={{verticalAlign:'middle', marginRight:4}}/> Retry
          </button>
        </div>
      )}

      {/* ── Info bar + collapsible legend ── */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:legendOpen ? 0 : 10, flexWrap:'wrap', gap:6}}>
        <div style={{fontSize:11, color:'var(--ink-3)'}}>
          {stale && <span style={{color:'var(--amber-ink)'}}>⚠ stale data · </span>}
          {fmtComputedAt && <span style={{opacity:.6}}>computed {fmtComputedAt}</span>}
        </div>
        <button onClick={() => setLegendOpen(o => !o)}
          style={{display:'flex', alignItems:'center', gap:4, background:'none', border:'.5px solid var(--hair-strong)',
            borderRadius:4, cursor:'pointer', padding:'3px 9px', color:'var(--ink-3)', fontSize:11, fontWeight:600}}>
          <span style={{fontSize:11}}>ⓘ</span> Methodology
          <span style={{fontSize:11, lineHeight:1, transition:'transform .2s', display:'inline-block',
            transform: legendOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}}>▾</span>
        </button>
      </div>
      {legendOpen && (
        <div style={{marginBottom:12, padding:'12px 16px', borderRadius:5,
          border:'.5px solid var(--hair-strong)', background:'var(--surface)', fontSize:11}}>

          {/* ── RS Ratio — full width intro ── */}
          <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>RS Ratio</div>
          <div style={{color:'var(--ink-3)', lineHeight:1.8, marginBottom:14}}>
            <div><strong style={{color:'var(--ink)'}}>Definition:</strong> RS Ratio = Asset price / NIFTY 50 price, both normalised to 1.0 at the start of the 90-session window (~4.5 months).</div>
            <div>A ratio <span style={{color:'var(--up-field-ink)', fontWeight:700}}>&gt; 1.0</span> means the asset has outperformed NIFTY since the window opened. A ratio <span style={{color:'var(--down-field-ink)', fontWeight:700}}>&lt; 1.0</span> means underperformance. <strong style={{color:'var(--ink)'}}>Window:</strong> 100 sessions (20W) · 20-day SMA applied to smooth noise.</div>
          </div>

          {/* ── Two-column grid (desktop) for the remaining sections ── */}
          <div style={{display:'grid', gridTemplateColumns: methWide ? '1fr 1fr' : '1fr', gap:'0 24px'}}>

            {/* Col 1 — Strength */}
            <div style={{marginBottom:14}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>Strength (RS Ratio Tier)</div>
              <div style={{display:'flex', flexDirection:'column', gap:4, color:'var(--ink-3)', lineHeight:1.7}}>
                <div><span style={{color:'var(--up-field-ink)', fontWeight:700}}>Strong</span> — RS Ratio &gt; 1.005</div>
                <div><span style={{color:'var(--amber-ink)', fontWeight:700}}>Equal</span> — RS Ratio between 0.995 – 1.005</div>
                <div><span style={{color:'var(--down-field-ink)', fontWeight:700}}>Weak</span> — RS Ratio &lt; 0.995</div>
              </div>
            </div>

            {/* Col 2 — Momentum */}
            <div style={{marginBottom:14}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>Momentum (RS vs 20D MA)</div>
              <div style={{display:'flex', flexDirection:'column', gap:4, color:'var(--ink-3)', lineHeight:1.7}}>
                <div><span style={{color:'var(--up-field-ink)', fontWeight:700}}>↗ Accelerating</span> — RS &gt; 20D MA by &gt;0.5%</div>
                <div><span style={{color:'var(--amber-ink)', fontWeight:700}}>→ Flat</span> — RS within ±0.5% of 20D MA</div>
                <div><span style={{color:'var(--down-field-ink)', fontWeight:700}}>↘ Decelerating</span> — RS &lt; 20D MA by &gt;0.5%</div>
              </div>
            </div>

            {/* Col 1 — CompoundPill */}
            <div style={{marginBottom:14}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>Compound Pill [Strength ↗]</div>
              <div style={{color:'var(--ink-3)', lineHeight:1.7}}>
                Combines Strength + Momentum in one chip. <strong style={{color:'var(--ink)'}}>Strong ↗</strong> = above NIFTY and still building. <strong style={{color:'var(--ink)'}}>Weak →</strong> = lags NIFTY but momentum is sideways.
              </div>
            </div>

            {/* Col 2 — WoW */}
            <div style={{marginBottom:14}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>WoW (Week-on-Week Momentum)</div>
              <div style={{color:'var(--ink-3)', lineHeight:1.7}}>
                Compares 1W RS change <em>today</em> vs 5 sessions ago. <span style={{color:'var(--up-field-ink)', fontWeight:700}}>↑</span> = improving. <span style={{color:'var(--down-field-ink)', fontWeight:700}}>↓</span> = deteriorating.
              </div>
            </div>

            {/* Col 1 — RS Change Columns */}
            <div style={{marginBottom:0}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>RS Change Columns</div>
              <div style={{color:'var(--ink-3)', lineHeight:1.7}}>
                <div><span style={{fontFamily:'var(--font-data)', color:'var(--ink)', fontWeight:600}}>RS 1W</span> — 5-session RS ratio change</div>
                <div><span style={{fontFamily:'var(--font-data)', color:'var(--ink)', fontWeight:600}}>RS 1M</span> — 21-session RS ratio change</div>
                <div><span style={{fontFamily:'var(--font-data)', color:'var(--ink)', fontWeight:600}}>RS 3M</span> — 63-session RS ratio change</div>
                <div style={{marginTop:4, opacity:.8}}>% changes. Green = gained vs NIFTY · Red = lagged.</div>
              </div>
            </div>

            {/* Col 2 — Top-Down Approach */}
            <div style={{marginBottom:0}}>
              <div style={{fontWeight:700, color:'var(--ink)', marginBottom:6, fontSize:12, letterSpacing:'.04em', textTransform:'uppercase'}}>Top-Down Approach (Sector → Stocks)</div>
              <div style={{color:'var(--ink-3)', lineHeight:1.7}}>
                <div>Sector RS vs NIFTY 50. Stock RS vs its sector index — isolates within-peer contribution.</div>
                <div style={{marginTop:4}}>Two pills per stock: <strong style={{color:'var(--ink)'}}>vs Sector</strong> and <strong style={{color:'var(--ink)'}}>vs NIFTY</strong>.</div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── Section 1: RELATIVE STRENGTH — BROAD ASSET CLASSES (always visible) ── */}
      <div style={{ margin:'14px 0 7px', display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', color:'var(--ink-3)' }}>
          Relative Strength — Broad Asset Classes <span style={{ fontWeight:500, opacity:.6 }}>(6)</span>
        </span>
        <InfoBtn>6 macro assets. Top 4 = capital flow basket (ranked among themselves). Bottom row = equity peer comparison. Crosshair syncs across all tiles.</InfoBtn>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16}}>
        <NiftyPriceCard history={niftyHistory} crosshairDate={crosshairDate}
          onCrosshairChange={setCrosshairDate} rank={focusRanks['nifty']}/>
        {focusRSChart(gsecEntry,      'G-Sec 10Y', focusRanks['gsec10'])}
        {focusRSChart(usdinrEntry,    'USD/INR',   focusRanks['usdinr'])}
        {focusRSChart(goldEntry,      'Gold',       focusRanks['gold'])}
        {focusRSChart(sp500Entry,     'S&P 500',   focusRanks['sp500'])}
        {focusRSChart(hangsengEntry,  'Hang Seng', focusRanks['hangseng'])}
      </div>

      {/* ── Section 2: RS RATIO vs NIFTY 50 — ALL ASSET CLASSES + SECTORS (collapsible) ── */}
      <SecHdr open={barChartOpen} onToggle={() => setBarChartOpen(o => !o)}
        infoText="Heatmap: fill = RS Strength, border = Momentum. Leaderboard: bar length = Strength, arrow = Momentum direction.">
        RS Ratio vs NIFTY 50 — All Asset Classes + Sectors
        {allData.length > 0 && <span style={{ fontWeight:500, opacity:.6, marginLeft:5 }}>({allData.length})</span>}
      </SecHdr>
      {barChartOpen && <RSTabView data={allData} onOpen={handleOpen}/>}

      {/* ── Section 3: RS RATIO CHART vs NIFTY 50 — ALL ASSET CLASSES + SECTORS (collapsible) ── */}
      <SecHdr open={chartsOpen} onToggle={() => setChartsOpen(o => !o)}
        infoText="All RS charts at a glance. Collapsed by default — open when you need a visual scan across the full universe."
        extra={chartsOpen && (
          <div style={{display:'flex', gap:4, flexWrap:'wrap'}}>
            {CHART_FILTERS.map(f => (
              <button key={f} onClick={() => setChartFilter(f)}
                style={{fontSize:11, padding:'2px 9px', borderRadius:10, cursor:'pointer',
                  border:'.5px solid var(--hair-strong)', fontWeight:chartFilter===f?700:400,
                  background:chartFilter===f?'var(--amber-ink)':'transparent',
                  color:chartFilter===f?'#fff':'var(--ink-3)', transition:'all .15s'}}>
                {f}
              </button>
            ))}
          </div>
        )}>
        RS Ratio Chart vs NIFTY 50 — All Asset Classes + Sectors
        {chartData.length > 0 && <span style={{ fontWeight:500, opacity:.6, marginLeft:5 }}>({chartData.length})</span>}
      </SecHdr>
      {chartsOpen && (
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:8, marginBottom:16}}>
          {chartData.map(d => (
            <RSChart
              key={d.asset.id}
              series={d.series}
              label={d.asset.name}
              stats={d.stats}
              onOpen={handleOpen}
              asset={d.asset}
              isProxy={d.isProxy}
              isError={d.isError}
              building={d.building}
            />
          ))}
        </div>
      )}

      {/* ── Section 4: TOP DOWN APPROACH — SECTOR > STOCKS — F&O STOCKS (collapsible, self-contained) ── */}
      <TopdownSection onOpen={handleOpen}/>

      {/* ── Section 5: RELATIVE STRENGTH & PRICE PERFORMANCE — F&O STOCKS (collapsible) ── */}
      <SecHdr open={rankingsOpen} onToggle={() => setRankingsOpen(o => !o)}
        infoText="Ordinal scoring across 6 dimensions (RS + Price at 1W/1M/3M). Lower total = stronger rank. Click row to see underlying ranks.">
        Relative Strength &amp; Price Performance — F&amp;O Stocks
        {allStockData.length > 0 && <span style={{ fontWeight:500, opacity:.6, marginLeft:5 }}>({allStockData.length})</span>}
      </SecHdr>
      {rankingsOpen && <RankingsTable allStockData={allStockData} onOpen={handleOpen}/>}

      {/* ── Section 6: MULTI TIME FRAME RS — consolidated scope toggle ── */}
      <SecHdr open={rsSummaryOpen} onToggle={() => setRsSummaryOpen(o => !o)}
        infoText="Multi-timeframe RS values for lookup. Signal column based on 1M RS.">
        Multi Time Frame RS Performance
        <span style={{ fontWeight:500, opacity:.6, marginLeft:5, fontSize:10 }}>
          ({mtfScope === 'assets' ? `${allData.length} assets & sectors` : `${allStockData.length} F&O stocks`})
        </span>
      </SecHdr>
      {rsSummaryOpen && (
        <>
          {/* Scope toggle */}
          <div style={{ display:'flex', gap:4, marginBottom:8 }}>
            {[['assets', 'Assets & Sectors'], ['stocks', 'F&O Stocks']].map(([v, l]) => (
              <button key={v} onClick={() => setMtfScope(v)}
                className={`pill${mtfScope === v ? ' on' : ''}`}
                style={{ fontSize:11, padding:'3px 10px' }}>
                {l}
              </button>
            ))}
          </div>
          {mtfScope === 'assets' && <RSTable data={allData} onOpen={handleOpen}/>}
          {mtfScope === 'stocks' && (
            <>
              <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:8, flexWrap:'wrap'}}>
                <input
                  value={stockSearch}
                  onChange={e => setStockSearch(e.target.value)}
                  placeholder="Search stock or sector..."
                  style={{fontSize:11, padding:'3px 8px', borderRadius:3,
                    border:'.5px solid var(--hair-strong)', background:'var(--ibg)',
                    color:'var(--ink)', outline:'none', width:160}}/>
                <Select value={stockSector} className="inp"
                  onChange={e => setStockSector(e.target.value)}>
                  {stockSectors.map(s => <option key={s}>{s}</option>)}
                </Select>
                <span style={{fontSize:11, color:'var(--ink-3)'}}>
                  {filteredStocks.length} of {allStockData.length} stocks
                </span>
                {(stockSearch || stockSector !== 'All') && (
                  <button onClick={() => { setStockSearch(''); setStockSector('All'); }}
                    style={{fontSize:11, padding:'1px 7px', borderRadius:3, cursor:'pointer',
                      border:'.5px solid var(--hair-strong)', color:'var(--ink-3)', background:'transparent'}}>
                    ✕ Clear
                  </button>
                )}
              </div>
              <StockRSTable rows={filteredStocks} onOpen={handleOpen}/>
            </>
          )}
        </>
      )}

      <div style={{fontSize:11, color:'var(--ink-3)', marginTop:10, paddingTop:6,
        borderTop:'.5px solid var(--hair)'}}>
        Real data via yfinance · refreshed daily at 16:00 IST
        {stale && ' · ⚠ showing cached values'}
        {' · '}
        <span title="S&P 500 (^GSPC) closes at ~01:30 IST and Hang Seng (^HSI) closes at ~13:00 IST. The 16:00 IST refresh captures their most recent close, but on the day they are still moving when the refresh runs their RS values reflect the prior session.">
          ⓘ S&P 500 &amp; Hang Seng: prior-session close at 16:00 IST refresh
        </span>
      </div>
    </div>
  );
}
